/**
 * ReviewsPage — Módulo de Opiniones/Reviews de Sera
 * Ruta: /reviews (requiere auth + reviews_enabled)
 * 3 pestañas: Listado, Estadísticas, Configuración
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useOrganization } from '@clerk/clerk-react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import {
  Star, StarHalf, CheckCircle, XCircle, Clock, MessageSquare,
  Filter, RefreshCw, ChevronLeft, ChevronRight, BarChart2,
  Settings, List, Trash2, Sparkles, Send, Image, X, ZoomIn,
  ShieldCheck, TrendingUp, TrendingDown, Mail, ToggleLeft, ToggleRight,
  AlertCircle, Search, ExternalLink, ChevronDown, ChevronUp,
  Layers, Copy, Check, Pencil, Plus, Upload, FileText, Download,
  ArrowRight, Package, AlertTriangle, Tag, Gift, Globe,
  Ban, PlayCircle, Unlock, Loader,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, Cell,
} from 'recharts';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';

// ── Helpers ────────────────────────────────────────────────────
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

function relativeTime(dateStr) {
  if (!dateStr) return '—';
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = Math.floor((now - then) / 1000);
  if (diff < 60) return 'hace un momento';
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `hace ${Math.floor(diff / 86400)} días`;
  return new Date(dateStr).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
}

function Stars({ rating, size = 16, showEmpty = true }) {
  const full = Math.floor(rating || 0);
  const empty = 5 - full;
  return (
    <span className="inline-flex items-center gap-0.5">
      {Array.from({ length: full }).map((_, i) => (
        <Star key={`f${i}`} size={size} className="text-amber-400 fill-amber-400" />
      ))}
      {showEmpty && Array.from({ length: empty }).map((_, i) => (
        <Star key={`e${i}`} size={size} className="text-gray-600" />
      ))}
    </span>
  );
}

const STATUS_CONFIG = {
  pending: { label: 'Pendiente', bg: 'bg-amber-500/15 text-amber-400 border-amber-500/30', icon: Clock },
  approved: { label: 'Aprobada', bg: 'bg-green-500/15 text-green-400 border-green-500/30', icon: CheckCircle },
  rejected: { label: 'Rechazada', bg: 'bg-red-500/15 text-red-400 border-red-500/30', icon: XCircle },
};

const LANG_FLAGS = { es: '🇪🇸', en: '🇬🇧', fr: '🇫🇷', de: '🇩🇪', it: '🇮🇹', pt: '🇵🇹', nl: '🇳🇱', pl: '🇵🇱', ca: '🏴' };

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.bg}`}>
      <Icon size={10} /> {cfg.label}
    </span>
  );
}

function StarRatingPicker({ value, onChange, size = 28 }) {
  const [hovered, setHovered] = useState(0);
  return (
    <span className="inline-flex gap-1">
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} type="button"
          onMouseEnter={() => setHovered(n)}
          onMouseLeave={() => setHovered(0)}
          onClick={() => onChange(n)}
          className="focus:outline-none transition-transform hover:scale-110"
        >
          <Star size={size}
            className={n <= (hovered || value || 0) ? 'text-amber-400 fill-amber-400' : 'text-gray-600'}
          />
        </button>
      ))}
    </span>
  );
}

// ── Lightbox ───────────────────────────────────────────────────
function Lightbox({ images, index, onClose }) {
  const [cur, setCur] = useState(index);
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);
  if (!images?.length) return null;
  const url = images[cur].startsWith('/') ? `${BACKEND}${images[cur]}` : images[cur];
  return (
    <div className="fixed inset-0 bg-black/90 z-[200] flex items-center justify-center" onClick={onClose}>
      <button className="absolute top-4 right-4 text-white/70 hover:text-white" onClick={onClose}><X size={24} /></button>
      {images.length > 1 && (
        <>
          <button className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2 bg-black/40 rounded-full"
            onClick={(e) => { e.stopPropagation(); setCur((cur - 1 + images.length) % images.length); }}>
            <ChevronLeft size={24} />
          </button>
          <button className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2 bg-black/40 rounded-full"
            onClick={(e) => { e.stopPropagation(); setCur((cur + 1) % images.length); }}>
            <ChevronRight size={24} />
          </button>
        </>
      )}
      <img src={url} alt="" className="max-h-[80vh] max-w-[80vw] object-contain rounded-lg" onClick={e => e.stopPropagation()} />
      {images.length > 1 && (
        <div className="absolute bottom-4 flex gap-2">
          {images.map((_, i) => (
            <button key={i} className={`w-2 h-2 rounded-full ${i === cur ? 'bg-white' : 'bg-white/40'}`}
              onClick={(e) => { e.stopPropagation(); setCur(i); }} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Review Detail Modal ────────────────────────────────────────
function ReviewDetailModal({ review, onClose, onApprove, onReject, onReplyPublish, orgId }) {
  const [replyText, setReplyText] = useState(review.reply?.text || review.reply_draft || '');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const generateAI = async () => {
    setGenerating(true);
    try {
      const res = await api.post(`/api/reviews/${review.review_id}/reply-ai?organization_id=${orgId}`);
      setReplyText(res.data.draft || '');
      toast.success('Borrador generado con IA');
    } catch (e) {
      toast.error('No se pudo generar la respuesta: ' + (e.response?.data?.detail || e.message));
    } finally {
      setGenerating(false);
    }
  };

  const publishReply = async () => {
    if (!replyText.trim()) { toast.error('Escribe una respuesta antes de publicar'); return; }
    setSaving(true);
    try {
      await api.post(`/api/reviews/${review.review_id}/reply?organization_id=${orgId}`, { text: replyText });
      toast.success('Respuesta publicada');
      onReplyPublish(review.review_id, replyText);
    } catch (e) {
      toast.error('Error al publicar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-gray-800 sticky top-0 bg-gray-900 z-10">
          <div className="flex items-center gap-3">
            <Stars rating={review.rating} size={20} />
            <span className="text-white font-bold text-lg">{review.rating}/5</span>
            <StatusBadge status={review.status} />
            {review.verified_purchase && (
              <span className="inline-flex items-center gap-1 text-xs text-emerald-400 border border-emerald-500/30 px-2 py-0.5 rounded-full bg-emerald-500/10">
                <ShieldCheck size={10} /> Compra verificada
              </span>
            )}
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={20} /></button>
        </div>

        <div className="px-6 py-4 space-y-5">
          {/* Customer + meta */}
          <div className="flex items-start justify-between">
            <div>
              <p className="text-white font-semibold">{review.customer_name || 'Cliente anónimo'}</p>
              <p className="text-gray-400 text-sm">{review.customer_email}</p>
              <p className="text-gray-500 text-xs mt-1">{relativeTime(review.created_at)} · {LANG_FLAGS[review.language] || review.language || ''}</p>
            </div>
            {review.order_id && (
              <div className="text-right">
                <p className="text-gray-400 text-xs">Pedido</p>
                <p className="text-gray-300 text-sm font-mono">#{review.order_id}</p>
              </div>
            )}
          </div>

          {/* Title + comment */}
          {review.title && <p className="text-white font-bold text-base">{review.title}</p>}
          {review.comment && (
            <p className="text-gray-300 text-sm leading-relaxed bg-gray-800/50 rounded-xl px-4 py-3 border border-gray-700/50">
              {review.comment}
            </p>
          )}

          {/* Photos */}
          {review.photos?.length > 0 && (
            <div>
              <p className="text-gray-400 text-xs mb-2 font-medium">FOTOS</p>
              <div className="flex gap-2 flex-wrap">
                {review.photos.map((url, i) => {
                  const fullUrl = url.startsWith('/') ? `${BACKEND}${url}` : url;
                  return (
                    <button key={i} onClick={() => setLightbox(i)}
                      className="relative group w-20 h-20 rounded-lg overflow-hidden border border-gray-700 hover:border-indigo-500 transition-colors">
                      <img src={fullUrl} alt="" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                        <ZoomIn size={16} className="text-white" />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* AI moderation */}
          {review.ai_moderation_result && (
            <div className={`rounded-xl px-4 py-3 border text-sm flex items-start gap-3 ${
              review.ai_moderation_result === 'approve'
                ? 'bg-green-500/8 border-green-500/20 text-green-300'
                : 'bg-red-500/8 border-red-500/20 text-red-300'
            }`}>
              <Sparkles size={14} className="mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">IA sugiere: {review.ai_moderation_result === 'approve' ? 'Aprobar' : 'Rechazar'}</p>
                {review.ai_moderation_reason && <p className="text-xs opacity-70 mt-0.5">{review.ai_moderation_reason}</p>}
              </div>
            </div>
          )}

          {/* Action buttons */}
          {review.status === 'pending' && (
            <div className="flex gap-3">
              <Button onClick={() => { onApprove(review.review_id); onClose(); }}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white" size="sm">
                <CheckCircle size={14} className="mr-1" /> Aprobar
              </Button>
              <Button onClick={() => { onReject(review.review_id); onClose(); }}
                variant="outline" className="flex-1 border-red-500/40 text-red-400 hover:bg-red-500/10" size="sm">
                <XCircle size={14} className="mr-1" /> Rechazar
              </Button>
            </div>
          )}

          {/* Reply section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-gray-300 font-medium text-sm">Respuesta pública</p>
              {review.reply?.text && <span className="text-xs text-green-400 bg-green-500/10 px-2 py-0.5 rounded-full">Publicada</span>}
            </div>
            {review.reply?.text && !replyText.startsWith(review.reply.text) && (
              <div className="bg-gray-800 rounded-xl px-4 py-3 text-gray-300 text-sm border border-gray-700">
                <p className="text-xs text-gray-500 mb-1 uppercase tracking-wide">Respuesta actual</p>
                {review.reply.text}
              </div>
            )}
            <textarea
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              placeholder="Escribe tu respuesta pública..."
              rows={3}
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-gray-200 text-sm resize-none focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500/30"
            />
            <div className="flex gap-2">
              <Button onClick={generateAI} disabled={generating} variant="outline"
                className="border-indigo-500/40 text-indigo-400 hover:bg-indigo-500/10 text-xs" size="sm">
                <Sparkles size={12} className="mr-1" /> {generating ? 'Generando...' : 'Generar con IA'}
              </Button>
              <Button onClick={publishReply} disabled={saving || !replyText.trim()}
                className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs" size="sm">
                <Send size={12} className="mr-1" /> {saving ? 'Publicando...' : 'Publicar respuesta'}
              </Button>
            </div>
          </div>
        </div>
      </div>
      {lightbox !== null && (
        <Lightbox images={review.photos || []} index={lightbox} onClose={() => setLightbox(null)} />
      )}
    </div>
  );
}

// ── Tab 1: Review List ─────────────────────────────────────────
function ReviewsListTab({ orgId }) {
  const [reviews, setReviews] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [detail, setDetail] = useState(null);
  const [sort, setSort] = useState('newest');
  const [filters, setFilters] = useState({ status: '', rating: '', review_type: '', language: '' });
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [pendingImportedCount, setPendingImportedCount] = useState(0);
  const [approvingAll, setApprovingAll] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const PER_PAGE = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ organization_id: orgId, page, per_page: PER_PAGE, sort });
      if (filters.status) params.set('status', filters.status);
      if (filters.rating) params.set('rating', filters.rating);
      if (filters.review_type) params.set('review_type', filters.review_type);
      if (filters.language) params.set('language', filters.language);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const res = await api.get(`/api/reviews?${params}`);
      setReviews(res.data.reviews || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      if (e.response?.status === 403) {
        // Feature not enabled — show empty state silently, no toast spam
        setReviews([]);
        setTotal(0);
      }
    } finally {
      setLoading(false);
    }
  }, [orgId, page, sort, filters, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  // Fetch count of pending imported reviews for the banner
  useEffect(() => {
    if (!orgId) return;
    api.get(`/api/reviews/import/pending-count?organization_id=${orgId}`)
      .then(r => setPendingImportedCount(r.data.pending || 0))
      .catch(() => {});
  }, [orgId]);

  const approveAllImported = async () => {
    setApprovingAll(true);
    try {
      const res = await api.post(`/api/reviews/bulk-approve-imported?organization_id=${orgId}`);
      toast.success(`${res.data.approved.toLocaleString()} opiniones importadas aprobadas`);
      setPendingImportedCount(0);
      load();
    } catch { toast.error('Error al aprobar las opiniones importadas'); }
    finally { setApprovingAll(false); }
  };

  const recalculateRatings = async () => {
    setRecalculating(true);
    try {
      const res = await api.post(`/api/reviews/recalculate-ratings?organization_id=${orgId}`);
      toast.success(`Valoraciones recalculadas — ${res.data.products_with_ratings} productos con puntuación`);
    } catch { toast.error('Error al recalcular las valoraciones'); }
    finally { setRecalculating(false); }
  };

  const setFilter = (key, val) => { setFilters(f => ({ ...f, [key]: val })); setPage(1); };

  const toggleSelect = (id) => {
    setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const toggleAll = () => {
    setSelected(s => s.size === reviews.length ? new Set() : new Set(reviews.map(r => r.review_id)));
  };

  const bulkAction = async (action) => {
    if (!selected.size) return;
    const ids = [...selected];
    const endpoint = action === 'approve' ? 'approve' : 'reject';
    try {
      await Promise.all(ids.map(id => api.put(`/api/reviews/${id}/${endpoint}?organization_id=${orgId}`)));
      toast.success(`${ids.length} review(s) ${action === 'approve' ? 'aprobadas' : 'rechazadas'}`);
      setSelected(new Set());
      load();
    } catch { toast.error('Error en la acción en lote'); }
  };

  const onApprove = async (id) => {
    await api.put(`/api/reviews/${id}/approve?organization_id=${orgId}`);
    toast.success('Review aprobada');
    load();
  };
  const onReject = async (id) => {
    await api.put(`/api/reviews/${id}/reject?organization_id=${orgId}`);
    toast.success('Review rechazada');
    load();
  };
  const onReplyPublish = (id, text) => {
    setReviews(rs => rs.map(r => r.review_id === id ? { ...r, reply: { text, replied_by: 'agent', replied_at: new Date().toISOString() } } : r));
    setDetail(d => d?.review_id === id ? { ...d, reply: { text } } : d);
  };

  const sortHeader = (key, label) => {
    const active = sort === key || sort === `-${key}`;
    const desc = sort === key;
    return (
      <button className={`flex items-center gap-1 ${active ? 'text-indigo-400' : 'text-gray-400'} hover:text-white text-xs font-medium`}
        onClick={() => setSort(sort === key ? `-${key}` : key)}>
        {label}
        {active ? (desc ? <ChevronDown size={12} /> : <ChevronUp size={12} />) : null}
      </button>
    );
  };

  const totalPages = Math.ceil(total / PER_PAGE);

  return (
    <div className="space-y-4">
      {/* Banner: pending imported reviews */}
      {pendingImportedCount > 0 && (
        <div className="flex items-center justify-between gap-4 bg-amber-900/20 border border-amber-700/40 rounded-xl px-4 py-3"
          data-testid="pending-imported-banner">
          <div className="flex items-center gap-2">
            <AlertCircle size={15} className="text-amber-400 flex-shrink-0" />
            <span className="text-amber-200 text-sm">
              <strong>{pendingImportedCount.toLocaleString()}</strong> opiniones importadas de Trusted Shops pendientes de aprobación
            </span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button size="sm" onClick={recalculateRatings} disabled={recalculating} variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-800 text-xs flex items-center gap-1.5"
              data-testid="recalculate-ratings-btn">
              {recalculating ? <><RefreshCw size={12} className="animate-spin" /> Recalculando…</> : <><RefreshCw size={12} /> Recalcular buscador</>}
            </Button>
            <Button size="sm" onClick={approveAllImported} disabled={approvingAll}
              className="bg-amber-600 hover:bg-amber-700 text-white text-xs flex items-center gap-1.5"
              data-testid="approve-all-imported-btn">
              {approvingAll
                ? <><RefreshCw size={12} className="animate-spin" /> Aprobando...</>
                : <><CheckCircle size={12} /> Aprobar todas las importadas</>}
            </Button>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 bg-gray-900/60 border border-gray-800 rounded-xl px-4 py-3">
        <select value={filters.status} onChange={e => setFilter('status', e.target.value)}
          className="bg-gray-800 border border-gray-700 text-gray-300 text-xs rounded-lg px-3 py-1.5 focus:outline-none focus:border-indigo-500">
          <option value="">Todos los estados</option>
          <option value="pending">Pendiente</option>
          <option value="approved">Aprobada</option>
          <option value="rejected">Rechazada</option>
        </select>
        <select value={filters.rating} onChange={e => setFilter('rating', e.target.value)}
          className="bg-gray-800 border border-gray-700 text-gray-300 text-xs rounded-lg px-3 py-1.5 focus:outline-none focus:border-indigo-500">
          <option value="">Todas las estrellas</option>
          {[5, 4, 3, 2, 1].map(n => <option key={n} value={n}>{n} ★</option>)}
        </select>
        <select value={filters.review_type} onChange={e => setFilter('review_type', e.target.value)}
          className="bg-gray-800 border border-gray-700 text-gray-300 text-xs rounded-lg px-3 py-1.5 focus:outline-none focus:border-indigo-500">
          <option value="">Todos los tipos</option>
          <option value="shop">Tienda</option>
          <option value="product">Producto</option>
        </select>
        <select value={filters.language} onChange={e => setFilter('language', e.target.value)}
          className="bg-gray-800 border border-gray-700 text-gray-300 text-xs rounded-lg px-3 py-1.5 focus:outline-none focus:border-indigo-500">
          <option value="">Todos los idiomas</option>
          {Object.entries(LANG_FLAGS).map(([k, v]) => <option key={k} value={k}>{v} {k.toUpperCase()}</option>)}
        </select>
        <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }}
          className="bg-gray-800 border border-gray-700 text-gray-300 text-xs rounded-lg px-3 py-1.5 focus:outline-none focus:border-indigo-500" />
        <span className="text-gray-600 text-xs">—</span>
        <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }}
          className="bg-gray-800 border border-gray-700 text-gray-300 text-xs rounded-lg px-3 py-1.5 focus:outline-none focus:border-indigo-500" />
        <button onClick={load} className="ml-auto text-gray-400 hover:text-white p-1.5 rounded-lg hover:bg-gray-800 transition-colors">
          <RefreshCw size={14} />
        </button>
      </div>

      {/* Bulk actions */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 bg-indigo-600/10 border border-indigo-500/30 rounded-xl px-4 py-2.5">
          <span className="text-indigo-300 text-sm font-medium">{selected.size} seleccionada(s)</span>
          <Button size="sm" onClick={() => bulkAction('approve')}
            className="bg-green-600 hover:bg-green-700 text-white text-xs py-1 px-3 h-auto">
            <CheckCircle size={12} className="mr-1" /> Aprobar
          </Button>
          <Button size="sm" variant="outline" onClick={() => bulkAction('reject')}
            className="border-red-500/40 text-red-400 hover:bg-red-500/10 text-xs py-1 px-3 h-auto">
            <XCircle size={12} className="mr-1" /> Rechazar
          </Button>
          <button onClick={() => setSelected(new Set())} className="ml-auto text-gray-500 hover:text-white"><X size={14} /></button>
        </div>
      )}

      {/* Table */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800 bg-gray-900/80">
                <th className="px-4 py-3 w-8">
                  <input type="checkbox" checked={selected.size === reviews.length && reviews.length > 0}
                    onChange={toggleAll}
                    className="rounded border-gray-600 bg-gray-800 accent-indigo-500" />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">CLIENTE</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">PRODUCTO</th>
                <th className="px-4 py-3 text-left">{sortHeader('rating', 'RATING')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 max-w-xs">COMENTARIO</th>
                <th className="px-4 py-3 text-left">{sortHeader('status', 'ESTADO')}</th>
                <th className="px-4 py-3 text-left">{sortHeader('newest', 'FECHA')}</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-400">EXTRAS</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="text-center py-12 text-gray-500">
                  <RefreshCw size={20} className="animate-spin mx-auto mb-2" /> Cargando...
                </td></tr>
              )}
              {!loading && reviews.length === 0 && (
                <tr><td colSpan={8} className="text-center py-12 text-gray-500">
                  <Star size={32} className="mx-auto mb-2 text-gray-700" />
                  <p>No hay reviews con los filtros seleccionados</p>
                </td></tr>
              )}
              {reviews.map((r, idx) => (
                <tr key={r.review_id}
                  className={`border-b border-gray-800/50 cursor-pointer transition-colors hover:bg-gray-800/40 ${idx % 2 === 0 ? '' : 'bg-gray-800/10'}`}
                  onClick={() => setDetail(r)}>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.has(r.review_id)}
                      onChange={() => toggleSelect(r.review_id)}
                      className="rounded border-gray-600 bg-gray-800 accent-indigo-500" />
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-gray-200 font-medium truncate max-w-[120px]">{r.customer_name || 'Anónimo'}</p>
                    <p className="text-gray-500 text-xs truncate max-w-[120px]">{r.customer_email}</p>
                  </td>
                  <td className="px-4 py-3">
                    {r.review_type === 'shop' ? (
                      <span className="text-gray-400 text-xs italic">Tienda general</span>
                    ) : (
                      <span className="text-gray-300 text-xs truncate max-w-[140px] block" title={r.product_name || r.product_id}>
                        {r.product_name || r.product_id || '—'}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <Stars rating={r.rating} size={12} showEmpty={false} />
                      <span className="text-gray-400 text-xs">{r.rating}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 max-w-xs">
                    <p className="text-gray-400 text-xs truncate">{(r.title ? `${r.title} — ` : '') + (r.comment || '').slice(0, 80)}{r.comment?.length > 80 ? '...' : ''}</p>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                  <td className="px-4 py-3">
                    <p className="text-gray-500 text-xs whitespace-nowrap">{relativeTime(r.created_at)}</p>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      {r.reply && <MessageSquare size={13} className="text-indigo-400" title="Tiene respuesta" />}
                      {r.verified_purchase && <ShieldCheck size={13} className="text-emerald-400" title="Compra verificada" />}
                      <span className="text-base">{LANG_FLAGS[r.language] || r.language || ''}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-gray-400">
          <span>{total} reviews en total</span>
          <div className="flex items-center gap-2">
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
              className="p-1.5 rounded-lg hover:bg-gray-800 disabled:opacity-30 transition-colors">
              <ChevronLeft size={16} />
            </button>
            <span className="text-gray-300">{page} / {totalPages}</span>
            <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)}
              className="p-1.5 rounded-lg hover:bg-gray-800 disabled:opacity-30 transition-colors">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {detail && (
        <ReviewDetailModal
          review={detail}
          orgId={orgId}
          onClose={() => setDetail(null)}
          onApprove={onApprove}
          onReject={onReject}
          onReplyPublish={onReplyPublish}
        />
      )}
    </div>
  );
}

// ── Tab 2: Stats ───────────────────────────────────────────────
function StatsTab({ orgId }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ organization_id: orgId });
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const res = await api.get(`/api/reviews/stats?${params}`);
      setStats(res.data);
    } catch (e) { if (e?.response?.status !== 403) toast.error('Error al cargar estadísticas'); }
    finally { setLoading(false); }
  }, [orgId, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="flex items-center justify-center py-20 text-gray-500">
      <RefreshCw size={24} className="animate-spin mr-3" /> Cargando estadísticas...
    </div>
  );

  if (!stats) return null;

  const totalStars = stats.stars1 + stats.stars2 + stats.stars3 + stats.stars4 + stats.stars5;
  const starDist = [5, 4, 3, 2, 1].map(n => ({
    stars: n,
    count: stats[`stars${n}`] || 0,
    pct: totalStars > 0 ? Math.round((stats[`stars${n}`] || 0) / totalStars * 100) : 0,
  }));

  const monthData = (stats.monthly_trend || []).map(m => ({
    name: `${['', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'][m.month]} ${String(m.year).slice(2)}`,
    reviews: m.count,
    media: m.avg,
  }));

  return (
    <div className="space-y-6">
      {/* Date filter */}
      <div className="flex items-center gap-3">
        <span className="text-gray-400 text-sm">Desde:</span>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
          className="bg-gray-800 border border-gray-700 text-gray-300 text-xs rounded-lg px-3 py-1.5 focus:outline-none focus:border-indigo-500" />
        <span className="text-gray-600">—</span>
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
          className="bg-gray-800 border border-gray-700 text-gray-300 text-xs rounded-lg px-3 py-1.5 focus:outline-none focus:border-indigo-500" />
        <button onClick={load} className="text-gray-400 hover:text-white p-1.5 rounded-lg hover:bg-gray-800"><RefreshCw size={14} /></button>
      </div>

      {/* ── Funnel section ── */}
      <div>
        <p className="text-white font-semibold mb-3">Embudo de solicitudes</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              label: 'Pendientes de enviar',
              value: stats.pending_queue_count ?? 0,
              sub: 'en cola de espera',
              color: 'amber',
              icon: <Clock size={16} className="text-amber-400" />,
            },
            {
              label: 'Solicitudes enviadas',
              value: stats.requests_sent ?? 0,
              sub: `${stats.conversion_rate || 0}% de respuesta`,
              color: 'indigo',
              icon: <Send size={16} className="text-indigo-400" />,
            },
            {
              label: 'Opiniones recibidas',
              value: stats.reviews_received ?? 0,
              sub: `${stats.approved || 0} aprobadas`,
              color: 'green',
              icon: <Star size={16} className="text-green-400" />,
            },
            {
              label: 'Solicitudes Google',
              value: stats.google_requests_sent ?? 0,
              sub: 'emails de reseña enviados',
              color: 'blue',
              icon: <Globe size={16} className="text-blue-400" />,
            },
          ].map((card, i) => (
            <div key={i} className={`bg-gray-900 border rounded-xl px-4 py-4 ${
              card.color === 'amber' ? 'border-amber-500/20' :
              card.color === 'green' ? 'border-green-500/20' :
              card.color === 'blue' ? 'border-blue-500/20' : 'border-indigo-500/20'
            }`}>
              <div className="flex items-center gap-2 mb-1">
                {card.icon}
                <p className="text-gray-400 text-xs">{card.label}</p>
              </div>
              <p className={`text-2xl font-bold ${
                card.color === 'amber' ? 'text-amber-400' :
                card.color === 'green' ? 'text-green-400' :
                card.color === 'blue' ? 'text-blue-400' : 'text-indigo-400'
              }`}>{card.value}</p>
              <p className="text-gray-500 text-xs mt-1">{card.sub}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Coupon impact section ── */}
      {(stats.coupons_issued > 0 || true) && (
        <div>
          <p className="text-white font-semibold mb-3">
            Impacto del cupón de incentivo
            {stats.coupon_prefix && (
              <span className="ml-2 font-mono text-xs bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 px-2 py-0.5 rounded-full">
                {stats.coupon_prefix}-*
              </span>
            )}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-gray-900 border border-gray-800 rounded-xl px-4 py-4">
              <div className="flex items-center gap-2 mb-1">
                <Tag size={15} className="text-indigo-400" />
                <p className="text-gray-400 text-xs">Cupones emitidos</p>
              </div>
              <p className="text-2xl font-bold text-indigo-400">{stats.coupons_issued ?? 0}</p>
              <p className="text-gray-500 text-xs mt-1">generados al recibir opinión</p>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-xl px-4 py-4">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle size={15} className="text-green-400" />
                <p className="text-gray-400 text-xs">Cupones canjeados</p>
              </div>
              <p className="text-2xl font-bold text-green-400">{stats.coupons_redeemed ?? 0}</p>
              <p className="text-gray-500 text-xs mt-1">
                {stats.coupons_issued > 0
                  ? `${Math.round((stats.coupons_redeemed / stats.coupons_issued) * 100)}% de tasa de canje`
                  : 'sin cupones emitidos'}
              </p>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-xl px-4 py-4">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp size={15} className="text-emerald-400" />
                <p className="text-gray-400 text-xs">Ingresos atribuidos</p>
              </div>
              <p className="text-2xl font-bold text-emerald-400">
                {(stats.coupons_revenue ?? 0).toLocaleString('es-ES', { style: 'currency', currency: stats.coupons_currency || 'EUR' })}
              </p>
              <p className="text-gray-500 text-xs mt-1">pedidos convertidos con cupón</p>
            </div>
          </div>
        </div>
      )}

      {/* KPI cards — original */}
      <div>
        <p className="text-white font-semibold mb-3">Moderación</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Rating medio', value: stats.avg_rating ? `${stats.avg_rating} ★` : '—', sub: `${stats.approved || 0} aprobadas`, color: 'amber' },
            { label: 'Total reviews', value: stats.total || 0, sub: `${stats.pending || 0} pendientes`, color: 'indigo' },
            { label: 'Sin moderar', value: stats.pending || 0, sub: 'necesitan moderación', color: 'amber' },
            { label: 'Conversión', value: `${stats.conversion_rate || 0}%`, sub: `${stats.request_completed || 0} / ${stats.request_total || 0} solicitudes`, color: 'green' },
          ].map((card, i) => (
            <div key={i} className={`bg-gray-900 border rounded-xl px-4 py-4 ${
              card.color === 'amber' ? 'border-amber-500/20' :
              card.color === 'green' ? 'border-green-500/20' : 'border-indigo-500/20'
            }`}>
              <p className="text-gray-400 text-xs mb-1">{card.label}</p>
              <p className={`text-2xl font-bold ${
                card.color === 'amber' ? 'text-amber-400' :
                card.color === 'green' ? 'text-green-400' : 'text-indigo-400'
              }`}>{card.value}</p>
              <p className="text-gray-500 text-xs mt-1">{card.sub}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Star distribution */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <p className="text-white font-semibold mb-4">Distribución de estrellas</p>
          <div className="space-y-3">
            {starDist.map(({ stars, count, pct }) => (
              <div key={stars} className="flex items-center gap-3">
                <span className="text-amber-400 text-sm font-medium w-6">{stars}★</span>
                <div className="flex-1 bg-gray-800 rounded-full h-2.5 overflow-hidden">
                  <div className="h-full bg-amber-400 rounded-full transition-all duration-700"
                    style={{ width: `${pct}%` }} />
                </div>
                <span className="text-gray-400 text-xs w-16 text-right">{count} ({pct}%)</span>
              </div>
            ))}
          </div>
        </div>

        {/* Monthly trend */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <p className="text-white font-semibold mb-4">Reviews por mes</p>
          {monthData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={monthData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6b7280' }} />
                <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: '#111827', border: '1px solid #374151', borderRadius: '8px', fontSize: '12px' }} />
                <Line type="monotone" dataKey="reviews" stroke="#6366f1" strokeWidth={2} dot={{ fill: '#6366f1', r: 3 }} name="Reviews" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-40 text-gray-600 text-sm">Sin datos</div>
          )}
        </div>
      </div>

      {/* Top / Bottom products */}
      {(stats.top_products?.length > 0 || stats.bottom_products?.length > 0) && (
        <div className="grid lg:grid-cols-2 gap-6">
          <ProductRankTable title="Productos mejor valorados" items={stats.top_products || []} positive />
          <ProductRankTable title="Productos peor valorados" items={stats.bottom_products || []} positive={false} />
        </div>
      )}
    </div>
  );
}

function ProductRankTable({ title, items, positive }) {
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-800 flex items-center gap-2">
        {positive ? <TrendingUp size={16} className="text-green-400" /> : <TrendingDown size={16} className="text-red-400" />}
        <p className="text-white font-semibold text-sm">{title}</p>
      </div>
      <div className="divide-y divide-gray-800/50">
        {items.slice(0, 10).map((p, i) => (
          <div key={i} className="flex items-center justify-between px-5 py-2.5">
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-gray-600 text-xs font-mono w-4">{i + 1}</span>
              <span className="text-gray-300 text-sm truncate max-w-[220px]" title={p.product_name || p.product_id}>
                {p.product_name || p.product_id || '—'}
              </span>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <Stars rating={p.avg_rating} size={12} showEmpty={false} />
              <span className={`text-sm font-bold ${positive ? 'text-green-400' : 'text-red-400'}`}>{p.avg_rating}</span>
              <span className="text-gray-500 text-xs">({p.count})</span>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-center py-8 text-gray-600 text-sm">Sin datos</div>
        )}
      </div>
    </div>
  );
}

// ── Tab 3: Settings ────────────────────────────────────────────
function SettingsTab({ orgId }) {
  const [settings, setSettings] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [statusInfo, setStatusInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [activeLang, setActiveLang] = useState('');
  const [emailCustom, setEmailCustom] = useState({});
  const [googleActiveLang, setGoogleActiveLang] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [sRes, stRes] = await Promise.all([
          api.get(`/api/reviews/settings?organization_id=${orgId}`),
          api.get(`/api/reviews/available-order-statuses?organization_id=${orgId}`),
        ]);
        const s = sRes.data;
        setSettings(s);
        setStatuses(stRes.data.statuses || []);
        setStatusInfo({
          platform: stRes.data.platform,
          has_synced_orders: stRes.data.has_synced_orders,
          has_labels: stRes.data.has_labels,
          last_labels_sync: stRes.data.last_labels_sync,
        });
        setEmailCustom({
          email_subject: s.email_subject || {},
          email_body_intro: s.email_body_intro || {},
        });
        if (s.available_languages?.length) {
          setActiveLang(s.available_languages[0]);
          setGoogleActiveLang(s.available_languages[0]);
        }
      } catch (e) { if (e?.response?.status !== 403) toast.error('Error al cargar la configuración'); }
      finally { setLoading(false); }
    };
    load();
  }, [orgId]);

  const syncOrders = async () => {
    setSyncing(true);
    try {
      const res = await api.post(`/api/reviews/sync-orders?organization_id=${orgId}`);
      const d = res.data;
      if (d.already_running) {
        toast.info('Sincronización ya en progreso, espera unos minutos…');
      } else if (d.started) {
        toast.success('Sincronización iniciada en segundo plano, puede tardar unos minutos');
      }
      // Reload statuses after a short delay to let background task start
      await new Promise(r => setTimeout(r, 3000));
      const stRes = await api.get(`/api/reviews/available-order-statuses?organization_id=${orgId}`);
      setStatuses(stRes.data.statuses || []);
      setStatusInfo({
        platform: stRes.data.platform,
        has_synced_orders: stRes.data.has_synced_orders,
        has_labels: stRes.data.has_labels,
        last_labels_sync: stRes.data.last_labels_sync,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al iniciar sincronización');
    } finally {
      setSyncing(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      // Merge google_review_request email customization into the settings object
      const googleCfg = settings.google_review_request || {};
      await api.put(`/api/reviews/settings?organization_id=${orgId}`, {
        ...settings,
        email_subject: emailCustom.email_subject,
        email_body_intro: emailCustom.email_body_intro,
        google_review_request: googleCfg,
      });
      toast.success('Configuración guardada');
    } catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const resetLang = (lang) => {
    if (!settings?.email_defaults) return;
    const defaults = settings.email_defaults[lang] || {};
    setEmailCustom(prev => ({
      email_subject: { ...prev.email_subject, [lang]: defaults.subject || '' },
      email_body_intro: { ...prev.email_body_intro, [lang]: defaults.body_intro || '' },
    }));
    toast.success(`Textos de "${lang}" restaurados al valor por defecto`);
  };

  if (loading) return <div className="flex justify-center py-20 text-gray-500"><RefreshCw size={20} className="animate-spin" /></div>;
  if (!settings) return null;

  const selectedStatus = statuses.find(s => String(s.state_id) === String(settings.trigger_order_status));
  const dynamicText = settings.trigger_order_status
    ? `Se enviará solicitud de review ${settings.trigger_delay_days || 3} días después de que el pedido pase a estado "${selectedStatus?.label || settings.trigger_order_status}"`
    : 'Configura el estado del pedido y los días para ver el resumen';

  return (
    <div className="max-w-3xl space-y-6">
      <div className="bg-gray-900 border border-gray-800 rounded-xl divide-y divide-gray-800">
        {/* Enable toggle — activates the whole module (widget + emails) */}
        <div className="flex items-center justify-between px-6 py-4">
          <div>
            <p className="text-white font-medium">Activar módulo de opiniones</p>
            <p className="text-gray-400 text-sm mt-0.5">Muestra el widget de reseñas en tu tienda y permite recoger opiniones</p>
          </div>
          <Switch checked={!!settings.enabled} onCheckedChange={v => setSettings(s => ({ ...s, enabled: v }))} data-testid="reviews-enabled-toggle" />
        </div>

        {/* Auto email toggle */}
        <div className="flex items-center justify-between px-6 py-4">
          <div>
            <p className="text-white font-medium">Solicitud automática por email</p>
            <p className="text-gray-400 text-sm mt-0.5">Envía un email post-compra pidiendo valoración cuando un pedido alcanza el estado configurado</p>
          </div>
          <Switch
            checked={settings.auto_send_emails !== false}
            onCheckedChange={v => setSettings(s => ({ ...s, auto_send_emails: v }))}
            data-testid="auto-email-toggle"
            disabled={!settings.enabled}
          />
        </div>

        {/* Trigger status */}
        <div className="px-6 py-4 space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-white font-medium block">Estado del pedido que dispara el envío</label>
            {statusInfo.platform === 'prestashop' && (
              <button
                onClick={syncOrders}
                disabled={syncing}
                data-testid="sync-orders-btn"
                className="flex items-center gap-1.5 text-xs text-indigo-400 hover:text-indigo-300 bg-indigo-500/10 hover:bg-indigo-500/20 border border-indigo-500/30 rounded-lg px-3 py-1.5 transition-colors disabled:opacity-50"
              >
                <RefreshCw size={12} className={syncing ? 'animate-spin' : ''} />
                {syncing ? 'Sincronizando...' : 'Actualizar'}
              </button>
            )}
          </div>

          {/* Info banner */}
          {statusInfo.platform === 'prestashop' && !statusInfo.has_labels && (
            <div className="flex items-start gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2.5">
              <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />
              <span>Pulsa <strong>Actualizar</strong> para cargar los nombres de los estados desde tu tienda PrestaShop</span>
            </div>
          )}
          {statusInfo.last_labels_sync && (
            <p className="text-gray-600 text-xs">
              Última sincronización: {new Date(statusInfo.last_labels_sync).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })}
            </p>
          )}

          {statuses.length === 0 ? (
            <p className="text-gray-500 text-sm">
              {statusInfo.platform
                ? 'No hay pedidos sincronizados todavía. Pulsa Actualizar para importar pedidos recientes.'
                : 'No hay conector configurado. Conecta tu tienda en la sección Conectores.'}
            </p>
          ) : (
            <select
              value={settings.trigger_order_status || ''}
              onChange={e => setSettings(s => ({ ...s, trigger_order_status: e.target.value }))}
              data-testid="trigger-status-select"
              className="bg-gray-800 border border-gray-700 text-gray-200 text-sm rounded-xl px-4 py-2.5 w-full max-w-sm focus:outline-none focus:border-indigo-500"
            >
              <option value="">Seleccionar estado...</option>
              {statuses.map(st => (
                <option key={st.state_id} value={String(st.state_id)}>
                  {st.has_label ? st.label : `Estado ${st.state_id}`}
                  {st.order_count > 0 ? ` (${st.order_count} pedidos)` : ''}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Delay days */}
        <div className="px-6 py-4 space-y-3">
          <label className="text-white font-medium block">Días de espera tras alcanzar ese estado</label>
          <div className="flex items-center gap-3">
            <input type="number" min={0} max={30} value={settings.trigger_delay_days ?? 3}
              onChange={e => setSettings(s => ({ ...s, trigger_delay_days: +e.target.value }))}
              className="bg-gray-800 border border-gray-700 text-gray-200 text-sm rounded-xl px-4 py-2.5 w-24 focus:outline-none focus:border-indigo-500" />
            <span className="text-gray-400 text-sm">días</span>
          </div>
          <p className="text-indigo-300 text-sm bg-indigo-500/10 border border-indigo-500/20 rounded-lg px-4 py-2.5">{dynamicText}</p>
        </div>

        {/* Max requests per customer */}
        <div className="px-6 py-4 space-y-2">
          <label className="text-white font-medium block">Días mínimos entre emails al mismo cliente</label>
          <div className="flex items-center gap-3">
            <input type="number" min={1} max={365} value={settings.max_requests_per_customer_days ?? 30}
              onChange={e => setSettings(s => ({ ...s, max_requests_per_customer_days: +e.target.value }))}
              className="bg-gray-800 border border-gray-700 text-gray-200 text-sm rounded-xl px-4 py-2.5 w-24 focus:outline-none focus:border-indigo-500" />
            <span className="text-gray-400 text-sm">días</span>
          </div>
        </div>

        {/* Review mode */}
        <div className="px-6 py-4 space-y-3">
          <label className="text-white font-medium block">Modo de recogida de opiniones</label>
          <div className="flex flex-col gap-2 sm:flex-row">
            {[
              { val: 'shop_and_products', label: 'Tienda + Productos', desc: 'Pide valoración de la tienda y de cada producto' },
              { val: 'shop_only', label: 'Solo tienda', desc: 'Solo valoración de la experiencia general de compra' },
            ].map(opt => (
              <label key={opt.val}
                className={`flex items-start gap-3 cursor-pointer border rounded-xl px-4 py-3 flex-1 transition-all ${
                  (settings.review_mode ?? 'shop_and_products') === opt.val
                    ? 'border-indigo-500 bg-indigo-500/10' : 'border-gray-700 hover:border-gray-600'
                }`}>
                <input type="radio" name="review_mode" value={opt.val}
                  checked={(settings.review_mode ?? 'shop_and_products') === opt.val}
                  onChange={() => setSettings(s => ({ ...s, review_mode: opt.val }))}
                  className="mt-1 accent-indigo-500" data-testid={`review-mode-${opt.val}`} />
                <div>
                  <p className="text-white text-sm font-medium">{opt.label}</p>
                  <p className="text-gray-400 text-xs mt-0.5">{opt.desc}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Initial sync days */}
        <div className="px-6 py-4 space-y-2">
          <label className="text-white font-medium block">Días de historial a importar en la primera sincronización</label>
          <div className="flex items-center gap-3">
            <input
              type="number" min={1} max={365}
              value={settings.initial_sync_days ?? 30}
              onChange={e => setSettings(s => ({ ...s, initial_sync_days: +e.target.value }))}
              data-testid="initial-sync-days-input"
              className="bg-gray-800 border border-gray-700 text-gray-200 text-sm rounded-xl px-4 py-2.5 w-24 focus:outline-none focus:border-indigo-500"
            />
            <span className="text-gray-400 text-sm">días (solo se usa la primera vez)</span>
          </div>
        </div>

        {/* AI options */}
        <div className="flex items-center justify-between px-6 py-4">
          <div>
            <p className="text-white font-medium">Auto-aprobación con IA</p>
            <p className="text-gray-400 text-sm mt-0.5">La IA aprueba automáticamente reviews que considera legítimas</p>
          </div>
          <Switch checked={!!settings.auto_approve_ai} onCheckedChange={v => setSettings(s => ({ ...s, auto_approve_ai: v }))} />
        </div>

        <div className="flex items-center justify-between px-6 py-4">
          <div>
            <p className="text-white font-medium">Respuesta automática a reviews positivas</p>
            <p className="text-gray-400 text-sm mt-0.5">Genera y publica automáticamente una respuesta a reviews de 4-5★</p>
          </div>
          <Switch checked={!!settings.auto_reply_positive} onCheckedChange={v => setSettings(s => ({ ...s, auto_reply_positive: v }))} />
        </div>
      </div>

      {/* Coupon reward section */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl divide-y divide-gray-800">
        <div className="px-6 py-4 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#6366f110' }}>
            <Gift size={16} className="text-indigo-400" />
          </div>
          <div className="flex-1">
            <p className="text-white font-semibold">Cupón de incentivo por review</p>
            <p className="text-gray-400 text-sm mt-0.5">Genera y muestra automáticamente un cupón de descuento al cliente tras enviar su opinión (solo compras verificadas)</p>
          </div>
          <Switch
            checked={!!(settings.coupon_reward?.enabled)}
            onCheckedChange={v => setSettings(s => ({
              ...s,
              coupon_reward: { ...(s.coupon_reward || {}), enabled: v }
            }))}
            data-testid="coupon-reward-toggle"
          />
        </div>

        {settings.coupon_reward?.enabled && (
          <>
            {/* Discount type + value */}
            <div className="px-6 py-4 space-y-3">
              <label className="text-white font-medium block">Tipo y valor del descuento</label>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex rounded-xl overflow-hidden border border-gray-700">
                  {[{ v: 'percentage', l: '% Porcentaje' }, { v: 'fixed', l: '€ Fijo' }].map(opt => (
                    <button
                      key={opt.v}
                      onClick={() => setSettings(s => ({ ...s, coupon_reward: { ...(s.coupon_reward || {}), discount_type: opt.v } }))}
                      data-testid={`coupon-discount-type-${opt.v}`}
                      className={`px-4 py-2 text-sm font-medium transition-colors ${
                        (settings.coupon_reward?.discount_type || 'percentage') === opt.v
                          ? 'bg-indigo-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'
                      }`}
                    >
                      {opt.l}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number" min={1} max={100}
                    value={settings.coupon_reward?.value ?? 10}
                    onChange={e => setSettings(s => ({ ...s, coupon_reward: { ...(s.coupon_reward || {}), value: +e.target.value } }))}
                    data-testid="coupon-value-input"
                    className="bg-gray-800 border border-gray-700 text-gray-200 text-sm rounded-xl px-4 py-2.5 w-24 focus:outline-none focus:border-indigo-500"
                  />
                  <span className="text-gray-400 text-sm">
                    {(settings.coupon_reward?.discount_type || 'percentage') === 'percentage' ? '%' : '€'}
                  </span>
                </div>
              </div>
            </div>

            {/* Expiry + min purchase + prefix */}
            <div className="px-6 py-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-gray-300 text-sm font-medium block">Días de validez</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number" min={1} max={365}
                    value={settings.coupon_reward?.expiration_days ?? 30}
                    onChange={e => setSettings(s => ({ ...s, coupon_reward: { ...(s.coupon_reward || {}), expiration_days: +e.target.value } }))}
                    data-testid="coupon-expiry-input"
                    className="bg-gray-800 border border-gray-700 text-gray-200 text-sm rounded-xl px-3 py-2.5 w-full focus:outline-none focus:border-indigo-500"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-gray-300 text-sm font-medium block">Compra mínima (€)</label>
                <input
                  type="number" min={0}
                  value={settings.coupon_reward?.min_purchase_amount ?? 0}
                  onChange={e => setSettings(s => ({ ...s, coupon_reward: { ...(s.coupon_reward || {}), min_purchase_amount: +e.target.value } }))}
                  data-testid="coupon-min-purchase-input"
                  className="bg-gray-800 border border-gray-700 text-gray-200 text-sm rounded-xl px-3 py-2.5 w-full focus:outline-none focus:border-indigo-500"
                  placeholder="0 = sin mínimo"
                />
              </div>
              <div className="space-y-2">
                <label className="text-gray-300 text-sm font-medium block">Prefijo del código</label>
                <input
                  type="text" maxLength={12}
                  value={settings.coupon_reward?.code_prefix ?? 'REVIEW'}
                  onChange={e => setSettings(s => ({ ...s, coupon_reward: { ...(s.coupon_reward || {}), code_prefix: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') } }))}
                  data-testid="coupon-prefix-input"
                  className="bg-gray-800 border border-gray-700 text-gray-200 text-sm rounded-xl px-3 py-2.5 w-full focus:outline-none focus:border-indigo-500 font-mono uppercase"
                  placeholder="REVIEW"
                />
              </div>
            </div>

            {/* Min rating to trigger */}
            <div className="px-6 py-4 space-y-3">
              <label className="text-white font-medium block">Estrellas mínimas para obtener el cupón</label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map(n => {
                  const minR = settings.coupon_reward?.min_rating_to_trigger ?? 1;
                  return (
                    <button
                      key={n}
                      onClick={() => setSettings(s => ({ ...s, coupon_reward: { ...(s.coupon_reward || {}), min_rating_to_trigger: n } }))}
                      data-testid={`coupon-min-rating-${n}`}
                      className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold transition-all border ${
                        n === minR
                          ? 'bg-amber-500 border-amber-500 text-white'
                          : 'border-gray-700 bg-gray-800 text-gray-400 hover:border-amber-500/50'
                      }`}
                    >
                      {n}★
                    </button>
                  );
                })}
              </div>
              <p className="text-gray-500 text-xs">
                {(settings.coupon_reward?.min_rating_to_trigger ?? 1) === 1
                  ? 'Todas las opiniones recibirán el cupón'
                  : `Solo clientes que valoren ${settings.coupon_reward?.min_rating_to_trigger}★ o más recibirán el cupón`}
              </p>
            </div>

            {/* Send by email toggle */}
            <div className="flex items-center justify-between px-6 py-4">
              <div>
                <p className="text-white font-medium">También enviar por email</p>
                <p className="text-gray-400 text-sm mt-0.5">Además de mostrarlo en pantalla, envía el código al cliente por correo</p>
              </div>
              <Switch
                checked={!!(settings.coupon_reward?.send_by_email)}
                onCheckedChange={v => setSettings(s => ({ ...s, coupon_reward: { ...(s.coupon_reward || {}), send_by_email: v } }))}
                data-testid="coupon-send-email-toggle"
              />
            </div>

            {/* Preview */}
            <div className="px-6 py-4">
              <p className="text-gray-400 text-xs mb-2">Vista previa del código generado:</p>
              <div className="flex items-center gap-3">
                <div className="font-mono font-bold text-lg tracking-widest px-4 py-2 rounded-xl bg-indigo-500/10 text-indigo-300 border border-indigo-500/20">
                  {(settings.coupon_reward?.code_prefix || 'REVIEW').toUpperCase()}-XXXXXX
                </div>
                <div className="text-xs text-gray-500">
                  {(settings.coupon_reward?.discount_type || 'percentage') === 'percentage'
                    ? `${settings.coupon_reward?.value ?? 10}% descuento`
                    : `${settings.coupon_reward?.value ?? 10}€ descuento`
                  }
                  {(settings.coupon_reward?.min_purchase_amount || 0) > 0
                    ? ` · mín. ${settings.coupon_reward?.min_purchase_amount}€`
                    : ''}
                  {` · válido ${settings.coupon_reward?.expiration_days ?? 30} días`}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Google Review request section */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl divide-y divide-gray-800">
        <div className="px-6 py-4 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-blue-500/10">
            <Star size={16} className="text-blue-400" />
          </div>
          <div className="flex-1">
            <p className="text-white font-semibold">Solicitud de reseña en Google</p>
            <p className="text-gray-400 text-sm mt-0.5">Envía un email con enlace a Google Reviews a los clientes que superen el mínimo de estrellas, con un retraso configurable</p>
          </div>
          <Switch
            checked={!!(settings.google_review_request?.enabled)}
            onCheckedChange={v => setSettings(s => ({
              ...s,
              google_review_request: { ...(s.google_review_request || {}), enabled: v }
            }))}
            data-testid="google-review-request-toggle"
          />
        </div>

        {settings.google_review_request?.enabled && (
          <>
            {/* Google URL */}
            <div className="px-6 py-4 space-y-2">
              <label className="text-white font-medium block">URL de tu página de reseñas en Google</label>
              <input
                type="url"
                value={settings.google_review_request?.google_review_url || ''}
                onChange={e => setSettings(s => ({ ...s, google_review_request: { ...(s.google_review_request || {}), google_review_url: e.target.value } }))}
                data-testid="google-review-url-input"
                placeholder="https://g.page/r/XXXXXXXX/review"
                className="w-full bg-gray-800 border border-gray-700 text-gray-200 text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:border-blue-500"
              />
              <p className="text-gray-500 text-xs">Encuéntrala en Google Business Profile → Reseñas → "Obtén más reseñas" → copiar enlace</p>
            </div>

            {/* Min rating + delay */}
            <div className="px-6 py-4 grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-3">
                <label className="text-white font-medium block">Estrellas mínimas para enviar</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map(n => {
                    const minR = settings.google_review_request?.min_rating_to_trigger ?? 4;
                    return (
                      <button
                        key={n}
                        onClick={() => setSettings(s => ({ ...s, google_review_request: { ...(s.google_review_request || {}), min_rating_to_trigger: n } }))}
                        data-testid={`google-min-rating-${n}`}
                        className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold transition-all border ${
                          n === minR
                            ? 'bg-blue-600 border-blue-600 text-white'
                            : 'border-gray-700 bg-gray-800 text-gray-400 hover:border-blue-500/50'
                        }`}
                      >
                        {n}★
                      </button>
                    );
                  })}
                </div>
                <p className="text-gray-500 text-xs">
                  {(settings.google_review_request?.min_rating_to_trigger ?? 4) === 1
                    ? 'Todos los clientes recibirán la solicitud'
                    : `Solo clientes con ${settings.google_review_request?.min_rating_to_trigger ?? 4}★ o más`}
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-white font-medium block">Días de retraso</label>
                <div className="flex items-center gap-3">
                  <input
                    type="number" min={0} max={30}
                    value={settings.google_review_request?.delay_days ?? 2}
                    onChange={e => setSettings(s => ({ ...s, google_review_request: { ...(s.google_review_request || {}), delay_days: +e.target.value } }))}
                    data-testid="google-delay-days-input"
                    className="bg-gray-800 border border-gray-700 text-gray-200 text-sm rounded-xl px-4 py-2.5 w-24 focus:outline-none focus:border-blue-500"
                  />
                  <span className="text-gray-400 text-sm">días después de la review</span>
                </div>
                <p className="text-gray-500 text-xs">
                  {(settings.google_review_request?.delay_days ?? 2) === 0
                    ? 'Se enviará inmediatamente al recibir la review'
                    : `Se enviará ${settings.google_review_request?.delay_days ?? 2} días después de que el cliente deje su opinión`}
                </p>
              </div>
            </div>

            {/* Customizable email text per language */}
            {settings.available_languages?.length > 0 && (
              <div>
                <div className="px-6 py-4 border-t border-gray-800">
                  <p className="text-white font-medium">Texto del email (opcional)</p>
                  <p className="text-gray-400 text-sm mt-0.5">Personaliza el asunto y el cuerpo del email por idioma. Si se deja vacío, se usa el texto por defecto.</p>
                </div>
                <div className="flex border-b border-gray-800 px-6 overflow-x-auto gap-0">
                  {settings.available_languages.map(lang => (
                    <button key={lang} onClick={() => setGoogleActiveLang(lang)}
                      className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                        googleActiveLang === lang
                          ? 'border-blue-500 text-blue-400'
                          : 'border-transparent text-gray-400 hover:text-white'
                      }`}>
                      {LANG_FLAGS[lang] || ''} {lang.toUpperCase()}
                    </button>
                  ))}
                </div>
                {googleActiveLang && (
                  <div className="px-6 py-5 space-y-4">
                    <div>
                      <label className="text-gray-300 text-sm font-medium block mb-2">Asunto del email</label>
                      <input
                        value={(settings.google_review_request?.email_subject || {})[googleActiveLang] || ''}
                        onChange={e => setSettings(s => ({
                          ...s,
                          google_review_request: {
                            ...(s.google_review_request || {}),
                            email_subject: {
                              ...(s.google_review_request?.email_subject || {}),
                              [googleActiveLang]: e.target.value,
                            }
                          }
                        }))}
                        data-testid={`google-email-subject-${googleActiveLang}`}
                        placeholder={`Ej: ¿Nos dejas una reseña en Google?`}
                        className="w-full bg-gray-800 border border-gray-700 text-gray-200 text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="text-gray-300 text-sm font-medium block mb-2">Cuerpo del email</label>
                      <textarea
                        rows={3}
                        value={(settings.google_review_request?.email_body || {})[googleActiveLang] || ''}
                        onChange={e => setSettings(s => ({
                          ...s,
                          google_review_request: {
                            ...(s.google_review_request || {}),
                            email_body: {
                              ...(s.google_review_request?.email_body || {}),
                              [googleActiveLang]: e.target.value,
                            }
                          }
                        }))}
                        data-testid={`google-email-body-${googleActiveLang}`}
                        placeholder="Ej: Tu opinión ya está publicada. Si tienes un momento, nos ayudaría mucho que la dejes también en Google."
                        className="w-full bg-gray-800 border border-gray-700 text-gray-200 text-sm rounded-xl px-4 py-2.5 resize-none focus:outline-none focus:border-blue-500"
                      />
                      <p className="text-gray-600 text-xs mt-1">Si se deja en blanco, se usa el texto predeterminado en el idioma del cliente</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Email customization by language */}
      {settings.available_languages?.length > 0 && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl">
          <div className="px-6 py-4 border-b border-gray-800">
            <p className="text-white font-semibold">Personalización del email por idioma</p>
            <p className="text-gray-400 text-sm mt-1">Personaliza el asunto y texto introductorio del email de solicitud de review</p>
          </div>
          {/* Language tabs */}
          <div className="flex border-b border-gray-800 px-6 overflow-x-auto gap-0">
            {settings.available_languages.map(lang => (
              <button key={lang} onClick={() => setActiveLang(lang)}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeLang === lang
                    ? 'border-indigo-500 text-indigo-400'
                    : 'border-transparent text-gray-400 hover:text-white'
                }`}>
                {LANG_FLAGS[lang] || ''} {lang.toUpperCase()}
              </button>
            ))}
          </div>
          {activeLang && (
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="text-gray-300 text-sm font-medium block mb-2">Asunto del email</label>
                <input
                  value={emailCustom.email_subject?.[activeLang] || ''}
                  onChange={e => setEmailCustom(prev => ({ ...prev, email_subject: { ...prev.email_subject, [activeLang]: e.target.value } }))}
                  placeholder={settings.email_defaults?.[activeLang]?.subject || `Asunto para ${activeLang}`}
                  className="w-full bg-gray-800 border border-gray-700 text-gray-200 text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="text-gray-300 text-sm font-medium block mb-2">Texto introductorio</label>
                <textarea
                  rows={3}
                  value={emailCustom.email_body_intro?.[activeLang] || ''}
                  onChange={e => setEmailCustom(prev => ({ ...prev, email_body_intro: { ...prev.email_body_intro, [activeLang]: e.target.value } }))}
                  placeholder={settings.email_defaults?.[activeLang]?.body_intro || `Texto de introducción para ${activeLang}`}
                  className="w-full bg-gray-800 border border-gray-700 text-gray-200 text-sm rounded-xl px-4 py-2.5 resize-none focus:outline-none focus:border-indigo-500"
                />
                <p className="text-gray-600 text-xs mt-1">Usa {'{name}'} para insertar el nombre del cliente</p>
              </div>
              <button onClick={() => resetLang(activeLang)}
                className="text-xs text-gray-500 hover:text-gray-300 underline underline-offset-2">
                Restaurar textos por defecto para {activeLang.toUpperCase()}
              </button>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="config-save-btn" className="bg-indigo-600 hover:bg-indigo-700 text-white px-8">
          {saving ? <RefreshCw size={14} className="animate-spin mr-2" /> : null}
          Guardar configuración
        </Button>
      </div>
    </div>
  );
}

// ── Manual Send Modal ──────────────────────────────────────────
function ManualSendModal({ orgId, onClose }) {
  const [form, setForm] = useState({ customer_email: '', customer_name: '', order_id: '', language: 'es' });
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!form.customer_email) { toast.error('El email es obligatorio'); return; }
    setSending(true);
    try {
      await api.post(`/api/reviews/request/send?organization_id=${orgId}`, form);
      toast.success('Solicitud enviada correctamente');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al enviar');
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-md p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-white font-bold text-lg">Enviar solicitud manual</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={20} /></button>
        </div>
        {[
          { key: 'customer_email', label: 'Email del cliente *', placeholder: 'cliente@email.com', type: 'email' },
          { key: 'customer_name', label: 'Nombre del cliente', placeholder: 'Juan García', type: 'text' },
          { key: 'order_id', label: 'ID del pedido (opcional)', placeholder: 'XXXXXX', type: 'text' },
        ].map(f => (
          <div key={f.key}>
            <label className="text-gray-300 text-sm font-medium block mb-1">{f.label}</label>
            <input type={f.type} placeholder={f.placeholder} value={form[f.key]}
              onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
              className="w-full bg-gray-800 border border-gray-700 text-gray-200 text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:border-indigo-500" />
          </div>
        ))}
        <div>
          <label className="text-gray-300 text-sm font-medium block mb-1">Idioma del email</label>
          <select value={form.language} onChange={e => setForm(p => ({ ...p, language: e.target.value }))}
            className="bg-gray-800 border border-gray-700 text-gray-200 text-sm rounded-xl px-4 py-2.5 w-full focus:outline-none focus:border-indigo-500">
            {Object.entries(LANG_FLAGS).map(([k, v]) => <option key={k} value={k}>{v} {k.toUpperCase()}</option>)}
          </select>
        </div>
        <div className="flex gap-3 pt-2">
          <Button variant="outline" onClick={onClose} className="flex-1 border-gray-700 text-gray-300">Cancelar</Button>
          <Button onClick={send} disabled={sending} className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white">
            <Mail size={14} className="mr-1" /> {sending ? 'Enviando...' : 'Enviar'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────
// ── Tab 4: Widget Builder ──────────────────────────────────────
const WIDGET_TYPES = [
  { key: 'carousel', label: 'Carrusel', color: 'bg-purple-500/20 text-purple-300', desc: 'Scroll horizontal de tarjetas de opinión' },
  { key: 'list',     label: 'Listado',  color: 'bg-indigo-500/20 text-indigo-300',  desc: 'Lista vertical (clásico)' },
  { key: 'mini_rating', label: 'Mini estrellas', color: 'bg-amber-500/20 text-amber-300', desc: 'Estrellas compactas para listas de productos' },
  { key: 'badge',    label: 'Badge',    color: 'bg-green-500/20 text-green-300',   desc: 'Puntuación global de la tienda' },
];
const WIDGET_SOURCES = [
  { key: 'shop',    label: 'Tienda',          desc: 'Todas las opiniones de la tienda' },
  { key: 'product', label: 'Producto',         desc: 'Solo las del producto en la página' },
  { key: 'both',    label: 'Tienda y producto', desc: 'Combinadas' },
];

// ── Shopify embed code helpers ─────────────────────────────────
function ShopifyCodeBlock({ code, copyKey, copied, onCopy }) {
  return (
    <div className="relative bg-gray-950 border border-gray-700 rounded-lg overflow-hidden">
      <pre className="text-xs text-green-300 font-mono p-3 overflow-x-auto whitespace-pre leading-relaxed">{code}</pre>
      <button
        onClick={() => onCopy(copyKey, code)}
        className="absolute top-2 right-2 p-1.5 rounded-md bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
        title="Copiar código">
        {copied === copyKey ? <Check size={13} className="text-green-400" /> : <Copy size={13} />}
      </button>
    </div>
  );
}

function WidgetsTab({ orgId }) {
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', type: 'carousel', source: 'shop' });
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(null);
  const [platform, setPlatform] = useState(null);
  const [expandedWidget, setExpandedWidget] = useState(null);

  const apiUrl = BACKEND;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [wRes, stRes] = await Promise.all([
        api.get(`/api/reviews/widgets?organization_id=${orgId}`),
        api.get(`/api/reviews/available-order-statuses?organization_id=${orgId}`),
      ]);
      setWidgets(wRes.data.widgets || []);
      setPlatform(stRes.data.platform || null);
    } catch (e) { if (e?.response?.status !== 403) toast.error('Error al cargar widgets'); }
    finally { setLoading(false); }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm({ name: '', type: 'carousel', source: 'shop' }); setShowModal(true); };
  const openEdit = (w) => { setEditing(w); setForm({ name: w.name, type: w.type, source: w.source }); setShowModal(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error('El nombre es obligatorio'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/api/reviews/widgets/${editing.widget_id}?organization_id=${orgId}`, form);
        toast.success('Widget actualizado');
      } else {
        await api.post(`/api/reviews/widgets?organization_id=${orgId}`, form);
        toast.success('Widget creado');
      }
      setShowModal(false);
      load();
    } catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const remove = async (w) => {
    if (!window.confirm(`¿Eliminar widget "${w.name}"?`)) return;
    try {
      await api.delete(`/api/reviews/widgets/${w.widget_id}?organization_id=${orgId}`);
      toast.success('Widget eliminado');
      load();
    } catch { toast.error('Error al eliminar'); }
  };

  const copySnippet = (key, text) => {
    navigator.clipboard.writeText(text).then(() => { setCopied(key); setTimeout(() => setCopied(null), 2000); });
  };

  const typeInfo = (key) => WIDGET_TYPES.find(t => t.key === key) || WIDGET_TYPES[0];
  const sourceInfo = (key) => WIDGET_SOURCES.find(s => s.key === key) || WIDGET_SOURCES[0];

  const globalInitSnippet = `<!-- Sera Reviews — añadir antes de </body> en theme.liquid -->
<script>
  window.SeraConfig = {
    orgId:  "${orgId}",
    apiUrl: "${apiUrl}"
  };
</script>
<script src="${apiUrl}/api/widget/sera-reviews.js" defer></script>`;

  const productPageSnippet = `{{- comment -}} En product.liquid o sections/main-product.liquid {{- endcomment -}}
<div class="sera-product-reviews"
     data-product-id="{{ product.id }}">
</div>`;

  const miniRatingSnippet = `{{- comment -}} En product-card.liquid (listados de colección) {{- endcomment -}}
<span class="sera-product-rating"
      data-product-id="{{ product.id }}">
</span>`;

  const widgetEmbedSnippet = (wid, source) => {
    if (source === 'product' || source === 'both') {
      return `<div data-sera-widget-id="${wid}"
     data-product-id="{{ product.id }}">
</div>`;
    }
    return `<div data-sera-widget-id="${wid}"></div>`;
  };

  const isShopify = platform === 'shopify';

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-white font-semibold text-lg">Widgets de Reviews</h2>
          <p className="text-gray-400 text-sm mt-0.5">
            {isShopify
              ? 'Crea widgets y pega los snippets en tu tema de Shopify.'
              : 'Crea widgets y asígnalos a hooks desde el módulo PrestaShop.'}
          </p>
        </div>
        <Button onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm"
          data-testid="create-widget-btn">
          <Plus size={14} className="mr-1.5" /> Nuevo widget
        </Button>
      </div>

      {/* Platform-specific instructions */}
      {isShopify ? (
        <div className="space-y-4">
          {/* Step 1: Global script */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 space-y-3"
            data-testid="shopify-global-script-section">
            <div className="flex items-center gap-2">
              <span className="bg-indigo-600 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center shrink-0">1</span>
              <p className="text-white font-semibold text-sm">Script global — pegar una sola vez en <code className="text-indigo-300 font-mono">theme.liquid</code></p>
            </div>
            <p className="text-gray-400 text-xs">En el editor de temas de Shopify (<strong className="text-gray-300">Online Store → Themes → Edit code</strong>), abre <code className="text-indigo-300">layout/theme.liquid</code> y pega esto justo antes de <code className="text-indigo-300">&lt;/body&gt;</code>.</p>
            <ShopifyCodeBlock code={globalInitSnippet} copyKey="global" copied={copied} onCopy={copySnippet} />
          </div>

          {/* Step 2: Place widgets */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2">
              <span className="bg-indigo-600 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center shrink-0">2</span>
              <p className="text-white font-semibold text-sm">Coloca los widgets en tu tema</p>
            </div>
            <p className="text-gray-400 text-xs mb-2">Para cada widget que crees abajo, aparecerá su snippet para pegarlo en la sección del tema donde quieras mostrarlo.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <p className="text-gray-300 text-xs font-semibold mb-1.5">Página de producto (reviews del producto)</p>
                <ShopifyCodeBlock code={productPageSnippet} copyKey="product_page" copied={copied} onCopy={copySnippet} />
              </div>
              <div>
                <p className="text-gray-300 text-xs font-semibold mb-1.5">Card de colección (mini rating)</p>
                <ShopifyCodeBlock code={miniRatingSnippet} copyKey="mini_rating" copied={copied} onCopy={copySnippet} />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-4 text-sm text-indigo-200 flex gap-3">
          <Layers size={18} className="shrink-0 mt-0.5 text-indigo-400" />
          <div>
            <strong className="text-indigo-300">¿Cómo funciona?</strong>
            <ol className="mt-1 space-y-0.5 text-gray-300 list-decimal list-inside">
              <li>Crea aquí un widget (elige tipo y fuente de opiniones).</li>
              <li>Copia su <strong>Widget ID</strong>.</li>
              <li>En tu PrestaShop → módulo Sera → panel "Asignar Widgets", elige el hook donde quieres mostrarlo.</li>
            </ol>
          </div>
        </div>
      )}

      {/* Widget grid */}
      {loading ? (
        <div className="text-gray-500 text-sm py-10 text-center">Cargando...</div>
      ) : widgets.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-gray-700 rounded-xl">
          <Layers size={32} className="mx-auto text-gray-600 mb-3" />
          <p className="text-gray-400 font-medium">Aún no tienes widgets</p>
          <p className="text-gray-500 text-sm mt-1 mb-4">
            {isShopify
              ? 'Crea tu primer widget para obtener el snippet HTML'
              : 'Crea tu primer widget para empezar a asignarlo a hooks'}
          </p>
          <Button onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm">
            <Plus size={14} className="mr-1.5" /> Crear primer widget
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {isShopify && (
            <p className="text-gray-500 text-xs">
              Pulsa <strong className="text-gray-300">"Ver snippet"</strong> en cada widget para ver el código HTML a pegar en tu tema.
            </p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {widgets.map(w => {
              const ti = typeInfo(w.type);
              const si = sourceInfo(w.source);
              const isExpanded = expandedWidget === w.widget_id;
              const embedCode = widgetEmbedSnippet(w.widget_id, w.source);
              return (
                <div key={w.widget_id} className="bg-gray-900 border border-gray-800 rounded-xl p-4 space-y-3"
                  data-testid={`widget-card-${w.widget_id}`}>
                  {/* Type + name */}
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-white font-semibold text-sm">{w.name}</p>
                      <p className="text-gray-400 text-xs mt-0.5">{ti.desc}</p>
                    </div>
                    <div className="flex gap-1.5">
                      <button onClick={() => openEdit(w)} className="p-1.5 rounded-md hover:bg-gray-800 text-gray-400 hover:text-white transition-colors">
                        <Pencil size={13} />
                      </button>
                      <button onClick={() => remove(w)} className="p-1.5 rounded-md hover:bg-red-500/20 text-gray-400 hover:text-red-400 transition-colors">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                  {/* Badges */}
                  <div className="flex gap-2 flex-wrap">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ti.color}`}>{ti.label}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-700 text-gray-300 font-medium">{si.label}</span>
                  </div>
                  {/* Widget ID */}
                  <div className="bg-gray-800 rounded-lg px-3 py-2 flex items-center gap-2">
                    <code className="text-xs text-indigo-300 flex-1 truncate font-mono">{w.widget_id}</code>
                    <button onClick={() => copySnippet(w.widget_id, w.widget_id)}
                      className="text-gray-400 hover:text-white transition-colors shrink-0"
                      title="Copiar Widget ID"
                      data-testid={`copy-widget-id-${w.widget_id}`}>
                      {copied === w.widget_id ? <Check size={13} className="text-green-400" /> : <Copy size={13} />}
                    </button>
                  </div>

                  {/* Shopify: embed snippet toggle */}
                  {isShopify && (
                    <div>
                      <button
                        onClick={() => setExpandedWidget(isExpanded ? null : w.widget_id)}
                        className="flex items-center gap-1.5 text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
                        data-testid={`toggle-embed-${w.widget_id}`}>
                        {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                        {isExpanded ? 'Ocultar snippet' : 'Ver snippet HTML'}
                      </button>
                      {isExpanded && (
                        <div className="mt-2">
                          <ShopifyCodeBlock
                            code={embedCode}
                            copyKey={`embed-${w.widget_id}`}
                            copied={copied}
                            onCopy={copySnippet}
                          />
                          {(w.source === 'product' || w.source === 'both') && (
                            <p className="text-gray-500 text-xs mt-1.5">
                              Pega este div en <code className="text-indigo-300">sections/main-product.liquid</code> (o donde quieras en la página de producto).
                            </p>
                          )}
                          {w.source === 'shop' && (
                            <p className="text-gray-500 text-xs mt-1.5">
                              Pega este div en cualquier sección de tu tema (homepage, footer, página de colección…).
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-gray-800">
              <h3 className="text-white font-semibold">{editing ? 'Editar widget' : 'Nuevo widget'}</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              {/* Name */}
              <div>
                <label className="text-gray-300 text-sm font-medium block mb-1.5">Nombre</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Ej: Carrusel homepage"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500"
                  data-testid="widget-name-input" />
              </div>
              {/* Type */}
              <div>
                <label className="text-gray-300 text-sm font-medium block mb-2">Tipo de widget</label>
                <div className="grid grid-cols-2 gap-2">
                  {WIDGET_TYPES.map(t => (
                    <button key={t.key} onClick={() => setForm(f => ({ ...f, type: t.key }))}
                      className={`p-3 rounded-lg border text-left transition-all ${form.type === t.key ? 'border-indigo-500 bg-indigo-500/10' : 'border-gray-700 bg-gray-800 hover:border-gray-600'}`}>
                      <p className={`text-xs font-semibold ${form.type === t.key ? 'text-indigo-300' : 'text-white'}`}>{t.label}</p>
                      <p className="text-xs text-gray-400 mt-0.5 leading-tight">{t.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
              {/* Source */}
              <div>
                <label className="text-gray-300 text-sm font-medium block mb-2">Fuente de opiniones</label>
                <div className="space-y-2">
                  {WIDGET_SOURCES.map(s => (
                    <button key={s.key} onClick={() => setForm(f => ({ ...f, source: s.key }))}
                      className={`w-full p-3 rounded-lg border text-left transition-all flex items-center gap-3 ${form.source === s.key ? 'border-indigo-500 bg-indigo-500/10' : 'border-gray-700 bg-gray-800 hover:border-gray-600'}`}>
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${form.source === s.key ? 'border-indigo-400' : 'border-gray-500'}`}>
                        {form.source === s.key && <div className="w-2 h-2 rounded-full bg-indigo-400" />}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">{s.label}</p>
                        <p className="text-xs text-gray-400">{s.desc}</p>
                      </div>
                    </button>
                  ))}
                </div>
                {form.type === 'list' && form.source !== 'product' && (
                  <p className="text-amber-400 text-xs mt-2 flex items-center gap-1">
                    <AlertCircle size={12} />
                    El tipo "Listado" requiere fuente "Producto" (necesita id_product en la URL).
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t border-gray-800">
              <Button onClick={() => setShowModal(false)} variant="outline" className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-800">
                Cancelar
              </Button>
              <Button onClick={save} disabled={saving} className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="widget-save-btn">
                {saving ? 'Guardando...' : (editing ? 'Guardar cambios' : 'Crear widget')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Import Reviews Modal ───────────────────────────────────────
function ImportReviewsModal({ orgId, onClose, onDone }) {
  const [step, setStep] = useState('upload'); // 'upload' | 'preview' | 'importing' | 'done'
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef();

  const handleFileDrop = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.xlsx')) {
      setError('Solo se aceptan archivos .xlsx (exportación de Trusted Shops)');
      return;
    }
    setFile(f);
    setError('');
  };

  const handlePreview = async () => {
    if (!file) return;
    setLoading(true);
    setError('');
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post(`/api/reviews/import/preview?organization_id=${orgId}`, fd);
      setPreviewData(res.data);
      setStep('preview');
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo leer el archivo. Asegúrate de que sea un export de Trusted Shops.');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file) return;
    setStep('importing');
    setError('');
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post(`/api/reviews/import/trusted-shops?organization_id=${orgId}`, fd);
      setResult(res.data);
      setStep('done');
      onDone?.();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error durante la importación');
      setStep('preview');
    }
  };

  const formatDate = (v) => {
    if (!v) return '—';
    try { return new Date(v).toLocaleDateString('es-ES'); } catch { return String(v); }
  };

  const StatusBadge = ({ s }) => {
    const cfg = { approved: 'bg-green-900/60 text-green-400', pending: 'bg-amber-900/60 text-amber-400', rejected: 'bg-red-900/60 text-red-400' };
    return <span className={`text-xs px-1.5 py-0.5 rounded ${cfg[s] || cfg.pending}`}>{s}</span>;
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      data-testid="import-reviews-modal">
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-600/20 flex items-center justify-center">
              <FileText size={16} className="text-indigo-400" />
            </div>
            <div>
              <h2 className="text-white font-semibold">Importar opiniones</h2>
              <p className="text-gray-400 text-xs">Trusted Shops · Exportación .xlsx</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors" data-testid="import-modal-close">
            <X size={20} />
          </button>
        </div>

        {/* Steps indicator */}
        <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-800/50">
          {[['upload', 'Subir archivo'], ['preview', 'Vista previa'], ['done', 'Resultado']].map(([s, label], i) => {
            const stepOrder = { upload: 0, preview: 1, importing: 1, done: 2 };
            const cur = stepOrder[step] ?? 0;
            const me = stepOrder[s] ?? i;
            const active = me === cur;
            const done = me < cur;
            return (
              <React.Fragment key={s}>
                {i > 0 && <div className={`flex-1 h-px ${done ? 'bg-indigo-600' : 'bg-gray-700'}`} />}
                <div className={`flex items-center gap-1.5 text-xs ${active ? 'text-indigo-400 font-medium' : done ? 'text-gray-400' : 'text-gray-600'}`}>
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${active ? 'bg-indigo-600 text-white' : done ? 'bg-gray-700 text-gray-400' : 'bg-gray-800 text-gray-600'}`}>
                    {done ? <Check size={10} /> : i + 1}
                  </div>
                  {label}
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">

          {/* ── STEP: upload ── */}
          {step === 'upload' && (
            <div className="space-y-4">
              <div
                className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
                  dragging ? 'border-indigo-500 bg-indigo-500/5' : 'border-gray-700 hover:border-gray-500'
                } ${file ? 'border-green-600/60 bg-green-900/10' : ''}`}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={e => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={e => { e.preventDefault(); setDragging(false); handleFileDrop(e.dataTransfer.files[0]); }}
                data-testid="import-dropzone">
                <input type="file" accept=".xlsx" ref={fileInputRef} className="hidden"
                  onChange={e => handleFileDrop(e.target.files[0])} />
                {file ? (
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-12 h-12 rounded-full bg-green-900/40 flex items-center justify-center">
                      <FileText size={22} className="text-green-400" />
                    </div>
                    <p className="text-white font-medium">{file.name}</p>
                    <p className="text-gray-400 text-sm">{(file.size / 1024 / 1024).toFixed(2)} MB · listo para previsualizar</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-12 h-12 rounded-full bg-gray-800 flex items-center justify-center">
                      <Upload size={22} className="text-gray-400" />
                    </div>
                    <p className="text-white font-medium">Arrastra tu exportación de Trusted Shops aquí</p>
                    <p className="text-gray-500 text-sm">o haz clic para seleccionar el archivo .xlsx</p>
                  </div>
                )}
              </div>

              <div className="bg-gray-800/50 rounded-lg p-3 text-xs text-gray-400 space-y-1">
                <p className="font-medium text-gray-300">¿Cómo exportar desde Trusted Shops?</p>
                <p>Ve a Trusted Shops → Valoraciones → Exportar → Exportar todas las valoraciones (.xlsx)</p>
                <p>Se esperan las hojas: <span className="text-gray-200">"Valoraciones de servicio"</span> y/o <span className="text-gray-200">"Valoraciones de producto"</span></p>
              </div>

              {error && (
                <div className="flex items-center gap-2 bg-red-900/30 border border-red-700/50 rounded-lg p-3 text-red-400 text-sm">
                  <AlertTriangle size={14} className="flex-shrink-0" /> {error}
                </div>
              )}
            </div>
          )}

          {/* ── STEP: preview ── */}
          {step === 'preview' && previewData && (
            <div className="space-y-5">
              {/* Summary cards */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-800/60 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-white">{previewData.total_reviews?.toLocaleString()}</p>
                  <p className="text-gray-400 text-xs mt-0.5">Opiniones a importar</p>
                </div>
                {previewData.sheets?.map(s => (
                  <div key={s.name} className="bg-gray-800/60 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-white">{s.total_rows?.toLocaleString()}</p>
                    <p className="text-gray-400 text-xs mt-0.5">{s.type === 'shop' ? 'De la tienda' : 'De producto'}</p>
                  </div>
                ))}
              </div>

              {/* Sheet previews */}
              {previewData.sheets?.map(sheet => (
                <div key={sheet.name} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Package size={14} className={sheet.type === 'shop' ? 'text-indigo-400' : 'text-amber-400'} />
                    <h3 className="text-white text-sm font-medium">{sheet.name}</h3>
                    <span className="text-gray-500 text-xs">({sheet.total_rows} filas · mostrando {sheet.sample?.length})</span>
                  </div>
                  {sheet.sample?.length > 0 ? (
                    <div className="overflow-x-auto rounded-lg border border-gray-700/60">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-gray-700/60 bg-gray-800/40">
                            <th className="text-left p-2 text-gray-400 font-medium">Cliente</th>
                            <th className="text-left p-2 text-gray-400 font-medium">Valoración</th>
                            <th className="text-left p-2 text-gray-400 font-medium">Fecha</th>
                            <th className="text-left p-2 text-gray-400 font-medium">Estado</th>
                            {sheet.type === 'product' && <th className="text-left p-2 text-gray-400 font-medium">Producto</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {sheet.sample.map((row, i) => (
                            <tr key={i} className="border-b border-gray-800/60 hover:bg-gray-800/20">
                              <td className="p-2 text-gray-300 max-w-[120px] truncate">{row.customer_name || '—'}</td>
                              <td className="p-2">
                                <div className="flex items-center gap-1">
                                  <Star size={10} className="text-amber-400 fill-amber-400" />
                                  <span className="text-white font-medium">{row.rating}</span>
                                </div>
                              </td>
                              <td className="p-2 text-gray-400">{formatDate(row.created_at)}</td>
                              <td className="p-2"><StatusBadge s={row.status} /></td>
                              {sheet.type === 'product' && (
                                <td className="p-2 text-gray-400 max-w-[120px] truncate">{row.product_name_original || row.sku_original || '—'}</td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-xs p-3 bg-gray-800/30 rounded-lg">No se pudieron parsear filas de muestra.</p>
                  )}
                </div>
              ))}

              {error && (
                <div className="flex items-center gap-2 bg-red-900/30 border border-red-700/50 rounded-lg p-3 text-red-400 text-sm">
                  <AlertTriangle size={14} className="flex-shrink-0" /> {error}
                </div>
              )}

              <div className="bg-amber-900/20 border border-amber-700/40 rounded-lg p-3 text-amber-300 text-xs flex items-start gap-2">
                <AlertCircle size={13} className="flex-shrink-0 mt-0.5" />
                <span>Las opiniones duplicadas (mismo ID de Trusted Shops) serán ignoradas automáticamente.</span>
              </div>
            </div>
          )}

          {/* ── STEP: importing ── */}
          {step === 'importing' && (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <div className="w-14 h-14 rounded-full bg-indigo-900/30 border border-indigo-600/40 flex items-center justify-center animate-pulse">
                <Download size={24} className="text-indigo-400" />
              </div>
              <div className="text-center">
                <p className="text-white font-medium">Importando opiniones…</p>
                <p className="text-gray-400 text-sm mt-1">Esto puede tardar unos segundos para archivos grandes</p>
              </div>
              <div className="w-48 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                <div className="h-full bg-indigo-600 rounded-full animate-[progressbar_2s_ease-in-out_infinite]" style={{width:'60%'}} />
              </div>
            </div>
          )}

          {/* ── STEP: done ── */}
          {step === 'done' && result && (
            <div className="space-y-4">
              <div className="flex flex-col items-center py-6 gap-2">
                <div className="w-14 h-14 rounded-full bg-green-900/30 border border-green-600/40 flex items-center justify-center">
                  <CheckCircle size={28} className="text-green-400" />
                </div>
                <p className="text-white font-semibold text-lg">¡Importación completada!</p>
                <p className="text-gray-400 text-sm">{result.total_imported?.toLocaleString()} nuevas opiniones importadas</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-green-900/20 border border-green-700/30 rounded-lg p-3">
                  <p className="text-green-400 text-xl font-bold">{result.shop_imported?.toLocaleString()}</p>
                  <p className="text-gray-400 text-xs">Opiniones de tienda</p>
                </div>
                <div className="bg-amber-900/20 border border-amber-700/30 rounded-lg p-3">
                  <p className="text-amber-400 text-xl font-bold">{result.product_imported?.toLocaleString()}</p>
                  <p className="text-gray-400 text-xs">Opiniones de producto</p>
                </div>
                <div className="bg-indigo-900/20 border border-indigo-700/30 rounded-lg p-3">
                  <p className="text-indigo-400 text-xl font-bold">{result.products_matched?.toLocaleString()}</p>
                  <p className="text-gray-400 text-xs">Productos vinculados</p>
                </div>
                <div className="bg-gray-800/60 rounded-lg p-3">
                  <p className="text-gray-300 text-xl font-bold">{result.duplicates_skipped?.toLocaleString()}</p>
                  <p className="text-gray-400 text-xs">Duplicados omitidos</p>
                </div>
              </div>

              {result.products_unmatched > 0 && (
                <div className="bg-amber-900/20 border border-amber-700/40 rounded-lg p-3 text-amber-300 text-xs flex items-start gap-2">
                  <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
                  <span>{result.products_unmatched} opiniones de producto no pudieron vincularse al catálogo (SKU no encontrado). Se importaron igualmente y serán visibles en el listado.</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-5 border-t border-gray-800">
          {step === 'done' ? (
            <Button onClick={onClose} className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="import-done-close-btn">
              Cerrar
            </Button>
          ) : step === 'preview' ? (
            <>
              <Button onClick={() => setStep('upload')} variant="outline"
                className="border-gray-700 text-gray-300 hover:bg-gray-800"
                data-testid="import-back-btn">
                Atrás
              </Button>
              <Button onClick={handleImport} disabled={loading}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-2"
                data-testid="import-confirm-btn">
                <Download size={14} /> Importar {previewData?.total_reviews?.toLocaleString()} opiniones
              </Button>
            </>
          ) : step === 'importing' ? (
            <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
              Importando, por favor espera…
            </div>
          ) : (
            <>
              <Button onClick={onClose} variant="outline"
                className="border-gray-700 text-gray-300 hover:bg-gray-800">
                Cancelar
              </Button>
              <Button onClick={handlePreview} disabled={!file || loading}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-2"
                data-testid="import-preview-btn">
                {loading ? <RefreshCw size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                {loading ? 'Analizando...' : 'Previsualizar'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ── Pending Queue Tab ────────────────────────────────────────────
function PendingQueueTab({ orgId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState({});
  const [blocking, setBlocking] = useState({});
  const [showBlocked, setShowBlocked] = useState(false);

  const [syncing, setSyncing] = useState(false);
  const [diagData, setDiagData] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);

  const runDiagnose = async () => {
    setDiagLoading(true);
    try {
      const res = await api.get(`/api/reviews/diagnose-orders?organization_id=${orgId}`);
      setDiagData(res.data);
    } catch (e) {
      toast.error('Error al ejecutar diagnóstico');
    } finally {
      setDiagLoading(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/reviews/pending-queue?organization_id=${orgId}`);
      setData(res.data);
    } catch (e) { if (e?.response?.status !== 403) toast.error('Error al cargar la cola'); }
    finally { setLoading(false); }
  };

  const syncOrders = async (forceFull = false) => {
    setSyncing(true);
    try {
      const res = await api.post(`/api/reviews/sync-orders?organization_id=${orgId}&force_full=${forceFull}`);
      if (res.data?.already_running) {
        toast.info('Sincronización ya en progreso…');
        _pollSyncStatus();
        return;
      }
      toast.success('Sincronización iniciada en segundo plano');
      _pollSyncStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al iniciar sincronización');
      setSyncing(false);
    }
  };

  const _pollSyncStatus = () => {
    const interval = setInterval(async () => {
      try {
        const s = await api.get(`/api/reviews/sync-status?organization_id=${orgId}`);
        const status = s.data?.status;
        if (status === 'completed') {
          clearInterval(interval);
          setSyncing(false);
          const synced = s.data?.orders_synced ?? 0;
          if (s.data?.error) {
            toast.error(`Sync completado con error: ${s.data.error}`);
          } else {
            toast.success(`Re-sincronización completada: ${synced} pedidos actualizados`);
          }
          await load();
        } else if (status !== 'running') {
          clearInterval(interval);
          setSyncing(false);
        }
      } catch {
        clearInterval(interval);
        setSyncing(false);
      }
    }, 4000);
    // Safety: stop polling after 15 min
    setTimeout(() => { clearInterval(interval); setSyncing(false); }, 900000);
  };

  useEffect(() => { load(); }, [orgId]);

  const sendNow = async (orderId) => {
    setSending(s => ({ ...s, [orderId]: true }));
    try {
      await api.post(`/api/reviews/pending-queue/send/${orderId}?organization_id=${orgId}`);
      toast.success('Email de solicitud enviado');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al enviar');
    } finally {
      setSending(s => ({ ...s, [orderId]: false }));
    }
  };

  const block = async (item) => {
    setBlocking(s => ({ ...s, [item.customer_email]: true }));
    try {
      await api.post(`/api/reviews/pending-queue/block?organization_id=${orgId}`, {
        customer_email: item.customer_email,
        customer_name: item.customer_name,
      });
      toast.success(`${item.customer_email} bloqueado`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al bloquear');
    } finally {
      setBlocking(s => ({ ...s, [item.customer_email]: false }));
    }
  };

  const unblock = async (email) => {
    try {
      await api.delete(`/api/reviews/pending-queue/block/${encodeURIComponent(email)}?organization_id=${orgId}`);
      toast.success('Cliente desbloqueado');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al desbloquear');
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-40">
      <Loader size={24} className="text-gray-500 animate-spin" />
    </div>
  );

  if (data?.settings_missing) return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center">
      <AlertCircle size={32} className="text-amber-400 mx-auto mb-3" />
      <p className="text-white font-semibold">Configuración incompleta</p>
      <p className="text-gray-400 text-sm mt-1">Define el estado de pedido que dispara el email en la pestaña <strong>Configuración</strong> para ver la cola de pendientes.</p>
    </div>
  );

  const items = data?.items || [];
  const blocked = data?.blocked || [];
  const delayDays = data?.delay_days || 3;
  const noEmailCount = data?.no_email_count || 0;
  const readyCount = items.filter(i => i.status === 'ready').length;

  return (
    <div className="space-y-6">
      {/* No-email warning banner */}
      {noEmailCount > 0 && items.length === 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex gap-3 items-start"
          data-testid="no-email-warning">
          <AlertCircle size={18} className="text-amber-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-amber-300 font-semibold text-sm">
              {noEmailCount} pedido{noEmailCount !== 1 ? 's' : ''} pendiente{noEmailCount !== 1 ? 's' : ''} sin email de cliente
            </p>
            <p className="text-amber-200/70 text-xs mt-1">
              Los pedidos están en la BD pero no se pudo resolver el email del cliente desde PrestaShop.
              Comprueba que la clave API tiene permiso <code className="bg-amber-900/30 px-1 rounded">read_customers</code> y pulsa "Re-sincronizar pedidos".
            </p>
            <button
              onClick={() => syncOrders(true)}
              disabled={syncing}
              className="mt-2.5 text-xs bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 text-amber-300 rounded-lg px-3 py-1.5 transition-colors flex items-center gap-1.5"
              data-testid="resync-orders-btn">
              <RefreshCw size={12} className={syncing ? 'animate-spin' : ''} />
              {syncing ? 'Sincronizando...' : 'Re-sincronizar pedidos (completo)'}
            </button>
            <p className="text-amber-200/50 text-xs mt-1.5">
              Vuelve a importar todos los pedidos y emails de clientes desde PrestaShop. Puede tardar unos minutos.
            </p>
          </div>
        </div>
      )}
      {noEmailCount > 0 && items.length > 0 && (
        <div className="bg-gray-900 border border-amber-500/20 rounded-lg px-4 py-2.5 flex items-center gap-2 text-sm">
          <AlertCircle size={14} className="text-amber-400 shrink-0" />
          <span className="text-amber-300">{noEmailCount} pedido{noEmailCount !== 1 ? 's' : ''} adicional{noEmailCount !== 1 ? 'es' : ''} sin email de cliente (no mostrados)</span>
          <button onClick={() => syncOrders(true)} disabled={syncing}
            className="ml-auto text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1">
            <RefreshCw size={11} className={syncing ? 'animate-spin' : ''} />
            {syncing ? 'Sincronizando...' : 'Re-sincronizar'}
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-white font-semibold">Cola de solicitudes pendientes</p>
          <p className="text-gray-400 text-sm mt-0.5">
            Clientes con pedido en el estado configurado esperando el email de solicitud de opinión ({delayDays} días de retraso)
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={runDiagnose} disabled={diagLoading}
            className="flex items-center gap-1.5 text-gray-400 hover:text-indigo-400 text-xs transition-colors border border-gray-700 rounded-lg px-2.5 py-1.5"
            data-testid="diagnose-btn" title="Diagnóstico de sincronización">
            <AlertCircle size={13} className={diagLoading ? 'animate-pulse' : ''} />
            {diagLoading ? 'Analizando...' : 'Diagnóstico'}
          </button>
          <button onClick={() => syncOrders(true)} disabled={syncing}
            className="flex items-center gap-1.5 text-xs border border-indigo-500/40 text-indigo-400 hover:bg-indigo-500/10 rounded-lg px-2.5 py-1.5 transition-colors"
            data-testid="resync-orders-header-btn">
            <RefreshCw size={13} className={syncing ? 'animate-spin' : ''} />
            {syncing ? 'Sincronizando...' : 'Re-sincronizar pedidos'}
          </button>
          <button onClick={load} className="flex items-center gap-1.5 text-gray-400 hover:text-white text-sm transition-colors">
            <RefreshCw size={14} /> Actualizar
          </button>
        </div>
      </div>

      {/* Diagnostic panel */}
      {diagData && (
        <div className="bg-gray-900 border border-gray-700 rounded-xl p-4 space-y-3" data-testid="diag-panel">
          <div className="flex items-center justify-between">
            <p className="text-white font-semibold text-sm">Resultado del diagnóstico</p>
            <button onClick={() => setDiagData(null)} className="text-gray-500 hover:text-white"><X size={14} /></button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Pedidos en ventana', value: diagData.total_orders_in_window, color: 'text-white' },
              { label: 'Con email', value: diagData.orders_with_email, color: 'text-green-400' },
              { label: 'Sin email', value: diagData.orders_without_email, color: diagData.orders_without_email > 0 ? 'text-amber-400' : 'text-white' },
              { label: 'Emails enviados', value: diagData.review_requests_sent, color: 'text-indigo-400' },
              { label: 'Clientes en BD', value: diagData.customers_in_db, color: 'text-white' },
              { label: 'Trigger estado', value: diagData.trigger_order_status ?? '—', color: 'text-white' },
              { label: 'Delay días', value: diagData.delay_days, color: 'text-white' },
              { label: 'Ventana máx.', value: diagData.earliest_date, color: 'text-gray-400' },
            ].map(({ label, value, color }) => (
              <div key={label} className="bg-gray-800 rounded-lg p-2.5">
                <p className="text-gray-400 text-xs">{label}</p>
                <p className={`font-bold text-sm mt-0.5 ${color}`}>{value}</p>
              </div>
            ))}
          </div>
          {diagData.ps_customers_api_test && (
            <div className={`rounded-lg p-3 text-xs ${diagData.ps_customers_api_test.http_status === 200 ? 'bg-green-500/10 border border-green-500/20' : 'bg-red-500/10 border border-red-500/30'}`}>
              <p className="font-semibold text-sm mb-1.5 text-white">Test API Clientes PrestaShop</p>
              <div className="space-y-0.5 font-mono text-gray-300">
                <p>HTTP: <span className={diagData.ps_customers_api_test.http_status === 200 ? 'text-green-400' : 'text-red-400'}>{diagData.ps_customers_api_test.http_status}</span></p>
                <p>Keys respuesta: {JSON.stringify(diagData.ps_customers_api_test.response_keys)}</p>
                <p>Campo customer: {diagData.ps_customers_api_test.customer_key_present ? '✓ presente' : '✗ ausente'}</p>
                <p>Email encontrado: <span className={diagData.ps_customers_api_test.email_found ? 'text-green-400' : 'text-red-400'}>{diagData.ps_customers_api_test.email_found || '— vacío'}</span></p>
                {diagData.ps_customers_api_test.error && <p className="text-red-400">Error: {diagData.ps_customers_api_test.error}</p>}
                <p className="text-gray-500 truncate">URL: {diagData.ps_customers_api_test.url_called}</p>
              </div>
            </div>
          )}
          {diagData.sample_order && (
            <p className="text-xs text-gray-500">Pedido de muestra: #{diagData.sample_order.order_id} — estado {diagData.sample_order.state_id} — fecha {diagData.sample_order.date} — email_en_bd: "{diagData.sample_order.customer_email || '(vacío)'}"</p>
          )}
        </div>
      )}

      {items.length === 0 ? (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-10 text-center">
          <CheckCircle size={32} className="text-green-400 mx-auto mb-3" />
          <p className="text-white font-semibold">Sin pendientes</p>
          <p className="text-gray-400 text-sm mt-1">No hay pedidos en cola. Los pedidos nuevos aparecerán aquí cuando alcancen el estado configurado.</p>
        </div>
      ) : (
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
          {readyCount > 0 && (
            <div className="px-5 py-3 bg-amber-500/10 border-b border-amber-500/20 flex items-center gap-2">
              <AlertCircle size={14} className="text-amber-400" />
              <p className="text-amber-300 text-sm font-medium">{readyCount} pedido{readyCount > 1 ? 's' : ''} listo{readyCount > 1 ? 's' : ''} para enviar — el cron los enviará automáticamente en la próxima ejecución</p>
            </div>
          )}
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="px-5 py-3 text-left text-gray-400 font-medium">Cliente</th>
                <th className="px-5 py-3 text-left text-gray-400 font-medium hidden md:table-cell">Pedido</th>
                <th className="px-5 py-3 text-left text-gray-400 font-medium hidden lg:table-cell">Estado</th>
                <th className="px-5 py-3 text-right text-gray-400 font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {items.map(item => (
                <tr key={item.order_id} className="hover:bg-gray-800/30 transition-colors">
                  <td className="px-5 py-3">
                    <p className="text-white font-medium">{item.customer_name || '—'}</p>
                    <p className="text-gray-400 text-xs mt-0.5">{item.customer_email}</p>
                  </td>
                  <td className="px-5 py-3 hidden md:table-cell">
                    <p className="text-gray-300 font-mono text-xs">{item.reference}</p>
                    <p className="text-gray-500 text-xs">{item.order_date}</p>
                  </td>
                  <td className="px-5 py-3 hidden lg:table-cell">
                    {item.status === 'ready' ? (
                      <span className="inline-flex items-center gap-1 bg-amber-500/15 text-amber-300 text-xs font-medium px-2.5 py-1 rounded-full">
                        <Clock size={10} /> Listo para enviar
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 bg-blue-500/10 text-blue-300 text-xs font-medium px-2.5 py-1 rounded-full">
                        <Clock size={10} /> {item.days_remaining > 0 ? `${item.days_remaining}d restantes` : 'Hoy'}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        data-testid={`pending-send-${item.order_id}`}
                        onClick={() => sendNow(item.order_id)}
                        disabled={sending[item.order_id]}
                        className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
                      >
                        {sending[item.order_id] ? <Loader size={12} className="animate-spin" /> : <Send size={12} />}
                        Enviar ahora
                      </button>
                      <button
                        data-testid={`pending-block-${item.order_id}`}
                        onClick={() => block(item)}
                        disabled={blocking[item.customer_email]}
                        className="flex items-center gap-1.5 bg-gray-800 hover:bg-red-900/40 border border-gray-700 hover:border-red-700/50 disabled:opacity-50 text-gray-400 hover:text-red-400 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
                      >
                        {blocking[item.customer_email] ? <Loader size={12} className="animate-spin" /> : <Ban size={12} />}
                        Bloquear
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Blocked customers */}
      {blocked.length > 0 && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
          <button
            onClick={() => setShowBlocked(v => !v)}
            className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-800/30 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Ban size={15} className="text-red-400" />
              <span className="text-white font-medium">Bloqueados ({blocked.length})</span>
              <span className="text-gray-500 text-sm">— no recibirán solicitudes de opinión</span>
            </div>
            {showBlocked ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
          </button>
          {showBlocked && (
            <div className="border-t border-gray-800">
              <table className="w-full text-sm">
                <tbody className="divide-y divide-gray-800/50">
                  {blocked.map(b => (
                    <tr key={b.customer_email} className="hover:bg-gray-800/20 transition-colors">
                      <td className="px-5 py-3">
                        <p className="text-gray-300">{b.customer_name || '—'}</p>
                        <p className="text-gray-500 text-xs">{b.customer_email}</p>
                      </td>
                      <td className="px-5 py-3 text-gray-500 text-xs hidden md:table-cell">
                        Bloqueado el {b.blocked_at ? new Date(b.blocked_at).toLocaleDateString('es-ES') : '—'}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <button
                          data-testid={`unblock-${b.customer_email}`}
                          onClick={() => unblock(b.customer_email)}
                          className="flex items-center gap-1.5 ml-auto bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-400 hover:text-white text-xs px-3 py-1.5 rounded-lg transition-colors"
                        >
                          <Unlock size={12} /> Desbloquear
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


export default function ReviewsPage() {
  const { organization } = useOrganization();
  const orgId = organization?.id;
  const [tab, setTab] = useState('list');
  const [pendingCount, setPendingCount] = useState(0);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [recalculatingHeader, setRecalculatingHeader] = useState(false);

  const handleRecalculate = async () => {
    setRecalculatingHeader(true);
    try {
      const res = await api.post(`/api/reviews/recalculate-ratings?organization_id=${orgId}`);
      toast.success(`Valoraciones actualizadas — ${res.data.products_with_ratings} productos con puntuación en el buscador`);
    } catch { toast.error('Error al recalcular las valoraciones'); }
    finally { setRecalculatingHeader(false); }
  };

  // Fetch pending count on load
  useEffect(() => {
    if (!orgId) return;
    const fetch = async () => {
      try {
        const res = await api.get(`/api/reviews?organization_id=${orgId}&status=pending&per_page=1`);
        setPendingCount(res.data.total || 0);
      } catch {}
    };
    fetch();
    const interval = setInterval(fetch, 60000);
    return () => clearInterval(interval);
  }, [orgId]);

  const TABS = [
    { key: 'list', label: 'Listado', icon: List },
    { key: 'stats', label: 'Estadísticas', icon: BarChart2 },
    { key: 'widgets', label: 'Widgets', icon: Layers },
    { key: 'settings', label: 'Configuración', icon: Settings },
    { key: 'pending', label: 'Pendientes', icon: Clock },
  ];

  if (!orgId) return (
    <div className="flex items-center justify-center h-64 text-gray-500">Cargando organización...</div>
  );

  return (
    <div className="min-h-screen bg-gray-950 p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-white text-2xl font-bold flex items-center gap-2">
            <Star size={22} className="text-amber-400 fill-amber-400" />
            Opiniones
            {pendingCount > 0 && (
              <span className="bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full ml-1"
                data-testid="reviews-pending-badge">
                {pendingCount > 99 ? '99+' : pendingCount}
              </span>
            )}
          </h1>
          <p className="text-gray-400 text-sm mt-1">Gestiona las opiniones y reviews de tus clientes</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setShowImportModal(true)} variant="outline"
            className="border-gray-700 text-gray-300 hover:bg-gray-800 text-sm"
            data-testid="import-reviews-btn">
            <Upload size={14} className="mr-2" /> Importar
          </Button>
          <Button onClick={handleRecalculate} disabled={recalculatingHeader} variant="outline"
            className="border-gray-700 text-gray-300 hover:bg-gray-800 text-sm"
            data-testid="recalculate-header-btn">
            {recalculatingHeader ? <RefreshCw size={14} className="mr-2 animate-spin" /> : <RefreshCw size={14} className="mr-2" />}
            Recalcular buscador
          </Button>
          <Button onClick={() => setShowSendModal(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm"
            data-testid="send-review-request-btn">
            <Mail size={14} className="mr-2" /> Enviar solicitud
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-800 gap-0">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              data-testid={`reviews-tab-${t.key}`}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === t.key ? 'border-indigo-500 text-indigo-400' : 'border-transparent text-gray-400 hover:text-white'
              }`}>
              <Icon size={15} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div data-testid={`reviews-tab-content-${tab}`}>
        {tab === 'list' && <ReviewsListTab orgId={orgId} />}
        {tab === 'stats' && <StatsTab orgId={orgId} />}
        {tab === 'widgets' && <WidgetsTab orgId={orgId} />}
        {tab === 'settings' && <SettingsTab orgId={orgId} />}
        {tab === 'pending' && <PendingQueueTab orgId={orgId} />}
        {tab === 'google_queue' && null /* reserved */}
      </div>

      {showSendModal && <ManualSendModal orgId={orgId} onClose={() => setShowSendModal(false)} />}
      {showImportModal && (
        <ImportReviewsModal
          orgId={orgId}
          onClose={() => setShowImportModal(false)}
          onDone={() => { /* list tab will refresh on next load */ }}
        />
      )}
    </div>
  );
}
